import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { useNetworkIdRedirect } from "./common/hooks";
import { Container, Row, Col, Tab, Button, Nav, Breadcrumb, Form } from "react-bootstrap";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import TabTableWithSearch from './TabTableWithSearch';
import searchIcon from "assets/svg/search.svg";
import dltWhiteIcon from "assets/svg/delete-white.svg";
import refreshIcon from "assets/svg/refresh-green.svg";
import hubIcon from "assets/svg/panl_hubIcon.svg";
import { IconButton } from "@mui/material";
import { DownloadDataUI } from "components/gateways/DownloadDataUI";
import { SensorTypeDropdown } from "components/gateways/SensorTypeDropdown";
import { ActuatorTypeDropdown } from "./common/ActuatorTypeDropdown";
import SensorIcon from "components/gateways/SensorIcon";
import { TimeRangeSelection } from "components/common/TimeRangeDropdown";
import moment from "moment";
import {
    getOneRegistry,
    getRegistryConfig,
    getGwAndLDSUStatus,
    getGatewayAlertNotification,
    getGWReading,
    getSensorLastReading,
    scanGateway,
    resetGateway,
    deleteRegistry,
    getAllLastBusReading,
    updateAlertNotification,
    updateSensorNotice,
    updateOneRegistry
} from "service/gatewayService";
import { getAuxiliaryHubsFromLdsus } from "./ldsuTransformers";
import {
    countDevicesByTagUIDSAIDs,
    addDeviceToUIDSAIDsMap,
    isUIDSetZoneNotRoom,
    isSensorFromEntityDevice,
    countSensorActuatorInstancesByTag,
    getIOControllerActuatorsFromConfig,
    selectEntityUUID,
    buildUIDToDeviceMap,
    hasMatchingRegistryNameOrNick,
} from "./common/deviceUtils";
import { 
    returnCheckboxStatusWithPush,
    returnDisabledStatusWithPush
} from "./common/alertUtils";
import { getLDSBusInfo, addOrRemoveConfirmLocationPopup } from "utils/gatewayFunctions";
import { buildPromQLQueryFromUIDSAIDsMap, selectUIDSAIDsForUID } from "./common/promqlUtils";
import { isHttpSuccess, convertSecondsToPrometheusTime } from "utils/functions";
import { getCalendarFormat } from "utils/datetime";
import { get } from "lodash";
import styledConst from "styles";
import { getControllerStatusInfo, getControllerStatusSortValue } from "./common/controllerStatus";
import { getControllerStatusFromReading2 } from "./common/controllerReadingUtils";
import { getRowToForContext, type NetworkRowToContext } from "./common/getRowTo";
import {
    createDefaultLast24HoursTimeRange,
    getPrometheusTimeRangeForSensorReadings,
    pickBestSensorBusReadingForSAID,
    mergeSensorLastReadingWithMinMaxFallback,
} from "./common/sensorStatsUtils";
import { fetchTags as fetchTagsCommon } from "./common/tagService";
import { formatSensorReadingCellValue, formatSensorMinMaxCellValue } from "./common/sensorValueFormatter";
import {
    HttpStatus,
    SCAN_REQUEST_MESSAGE,
    SCAN_GATEWAY_FAIL_MESSAGE,
    ACTION_FAIL_MESSAGE,
    HeartbeatUpdateAfter,
    CHART_CUSTOM_RANGE_OPTION,
} from "constant";
import { showErrorAlert, showSuccessAlert, getAPIError } from "utils/alert";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import { WalletAlertComponent, walletModalTrigger } from "hooks/wallet";
import { useCheckIfBasicPlan } from "hooks/useCheckIfBasicPlan";
import { LoadingModal } from "components/modals/ModalTemplate";
import { useOrganisation } from "hooks/organization";
import WebsocketQuery from "service/websocketQuery";
import LocalStorageService from "service/localStorageService";
import CommonModal from "components/common/CommonModal";
import "./NetworkDetails.css";

type ColumnRenderer = (row: any) => React.ReactNode;
type EntityTabKey = "zones" | "rooms" | "groups" | "hubs";
type EntityTagType = "zone" | "room" | "group" | "hub";

const tabKeys = [
    "zones",
    "rooms",
    "groups",
    "sensors",
    "controllers",
    "auxiliaryHubs",
    "hubs",
];

const tabLabels: { [key: string]: string } = {
    zones: "Zones",
    rooms: "Rooms",
    groups: "Groups",
    sensors: "Sensors",
    controllers: "Actuators",
    auxiliaryHubs: "Auxiliary Hubs",
    hubs: "Hubs",
};

const WS = new WebsocketQuery();

type TagOnOffStatus = "On" | "Off";
type TagStatus = TagOnOffStatus | "Unknown";

const isDeviceOnlineForTagStatus = (statusValue: any): boolean => {
    if (Array.isArray(statusValue) && statusValue.length > 1) {
        const status = String(statusValue[1]);
        return status !== "0" && status !== "0.0";
    }

    if (typeof statusValue === "string") {
        return statusValue !== "0" && statusValue !== "0.0";
    }

    if (typeof statusValue === "number") {
        return statusValue !== 0;
    }

    return false;
};

const determineTagStatusFromWSResponse = (response: any): TagOnOffStatus => {
    const resultData = response?.data || response?.result || [];
    const dataArray = Array.isArray(resultData) ? resultData : [];

    if (dataArray.length === 0) {
        // No data returned from PromQL → treat as unknown rather than explicitly "Off"
        return "Unknown" as TagOnOffStatus;
    }

    for (const item of dataArray) {
        const statusValue = item?.value;
        if (isDeviceOnlineForTagStatus(statusValue)) {
            return "On";
        }
    }

    return "Off";
};

const queryTagStatusFromUIDSAIDs = async (
    gatewayId: string,
    uidSaids: Map<string, Set<number>>
): Promise<TagStatus> => {
    try {
        const query = buildPromQLQueryFromUIDSAIDsMap(gatewayId, uidSaids);
        if (!query) {
            return "Unknown";
        }

        const orgId = LocalStorageService.getItem("orgId");

        if (!WS.isReady(orgId)) {
            await WS.initConnection(orgId);
        }

        const params = {
            query,
            time: moment().unix(),
        };

        const response = await WS.fetch(params);
        const status = determineTagStatusFromWSResponse(response);
        return status;
    } catch (error) {
        if (error instanceof Error) {
            // Intentionally ignore and return default value
        }
        return "Unknown";
    }
};

const NetworkDetails = (): JSX.Element => {
    const { id } = useParams<{ id: string }>();
    const [activeTab, setActiveTab] = useState("zones");
    const [network, setNetwork] = useState<any>(null);
    const [ldsuAuxiliaryHubs, setLdsuAuxiliaryHubs] = useState<any[]>([]);
    const [rawSensorList, setRawSensorList] = useState<any[]>([]);
    const [rawActuatorList, setRawActuatorList] = useState<any[]>([]);
    const [tagUIDSAIDsMap, setTagUIDSAIDsMap] = useState<Map<string, Map<string, Map<string, Set<number>>>>>(new Map());
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isEntityStatusRefreshing, setIsEntityStatusRefreshing] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false); // download info modal
    const [modalShow, setModalShow] = useState(false); // delete confirm modal
    const [networkName, setNetworkName] = useState("");
    const [isNetworkNameEditing, setIsNetworkNameEditing] = useState(false);
    const history = useHistory();
    const { currentOrgId } = useOrganisation();
    const { validateAndRedirect, isValid } = useNetworkIdRedirect(id);


    // Sensor-specific state
    const [selectedSensorTypes, setSelectedSensorTypes] = useState<string[]>([]);
    const [selectedActuatorTypes, setSelectedActuatorTypes] = useState<string[]>([]);
    const [timeRange] = useState<TimeRangeSelection>(() =>
        createDefaultLast24HoursTimeRange()
    );

    // Alert state management
    const [alertUUIDList, setAlertUUIDList] = useState<any[]>([]);
    const [isAlertLoading, setIsAlertLoading] = useState(false);
    const { IS_BASIC_PLAN } = useCheckIfBasicPlan();
    const networkRef = useRef<any>(null);
    const tagUIDSAIDsMapRef = useRef<Map<string, Map<string, Map<string, Set<number>>>>>(new Map());

    useEffect(() => {
        networkRef.current = network;
    }, [network]);

    useEffect(() => {
        tagUIDSAIDsMapRef.current = tagUIDSAIDsMap;
    }, [tagUIDSAIDsMap]);

    // Sensor types for filtering
    const sensorTypes = useMemo(() => {
        if (activeTab !== 'sensors' || !rawSensorList.length) {
            return [];
        }
        const types: { [id: string]: string } = {};
        for (const row of rawSensorList) {
            // Check all possible field names for sensor type
            const type = row.sensor_type ?? row.type ?? row.sensorType;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        const options = Object.keys(types).map((id) => ({ id, name: id }));
        return options;
    }, [rawSensorList, activeTab]);

    // Actuator types for filtering
    const actuatorTypes = useMemo(() => {
        if (activeTab !== 'controllers' || !rawActuatorList.length) {
            return [];
        }
        const types: { [id: string]: string } = {};
        for (const row of rawActuatorList) {
            const type = row.type;
            if (type && !types[type]) {
                types[type] = type;
            }
        }
        const options = Object.keys(types).map((id) => ({ id, name: id }));
        return options;
    }, [rawActuatorList, activeTab]);

    // Filter sensors by selected types
    const filteredSensorList = useMemo(() => {
        if (!rawSensorList.length || selectedSensorTypes.length === 0) {
            return rawSensorList;
        }
        return rawSensorList.filter(sensor => {
            // Check all possible field names for sensor type
            const sensorType = sensor.sensor_type ?? sensor.type ?? sensor.sensorType;
            return selectedSensorTypes.includes(sensorType);
        });
    }, [rawSensorList, selectedSensorTypes]);

    // Filter actuators by selected types
    const filteredActuatorList = useMemo(() => {
        if (!rawActuatorList.length || selectedActuatorTypes.length === 0) {
            return rawActuatorList;
        }
        return rawActuatorList.filter(actuator =>
            selectedActuatorTypes.includes(actuator.type)
        );
    }, [rawActuatorList, selectedActuatorTypes]);

    // Alert management functions
    // Helper function to find alert in the list
    const findAlertInList = useCallback((source: string, said: number) => {
        for (let i = 0; i < alertUUIDList.length; i++) {
            if (
                source === alertUUIDList[i].source &&
                String(said) === String(alertUUIDList[i].said)
            ) {
                return {
                    index: i,
                    uuid: alertUUIDList[i].uuid,
                    status: alertUUIDList[i].status,
                    gatewayUUID: alertUUIDList[i].gatewayUUID,
                    notice: alertUUIDList[i].notice,
                };
            }
        }
        return null;
    }, [alertUUIDList]);

    // Helper function to prepare notice body for update
    const prepareNoticeBody = useCallback((currentNotice: any, newEnabledStatus: boolean) => {
        const existingEnabled = currentNotice.enabled ?? [false, false];
        const existingSubject = currentNotice.subject ?? ["", ""];
        const existingText = currentNotice.text ?? ["", ""];
        let messageEnabled = existingEnabled;
        let subject = existingSubject;
        let text = existingText;

        if (newEnabledStatus && !existingEnabled[0] && !existingEnabled[1]) {
            messageEnabled = [true, false];
            if (!existingSubject[0] && !existingText[0]) {
                subject = ["Alert Activated", "The sensor alert has been activated."];
                text = ["Alert Activated", "The sensor alert has been activated."];
            } else {
                subject = [existingSubject[0] ?? "Alert Activated", existingSubject[1] ?? ""];
                text = [existingText[0] ?? "The sensor alert has been activated.", existingText[1] ?? ""];
            }
        }

        return {
            enabled_email: false,
            recipients_email: [],
            enabled_sms: false,
            recipients_sms: [],
            enabled_push: newEnabledStatus,
            recipients_push: currentNotice.recipients_push ?? [],
            subject: subject,
            text: text,
            enabled: messageEnabled,
        };
    }, []);

    const validateRecipientsList = useCallback((notificationList: any[] = []) => {
        if (notificationList.length > 0) {
            return true;
        }

        showErrorAlert({
            message: "At least one recipient must be selected.",
        });
        return false;
    }, []);

    // Helper function to update notice configuration
    const updateNoticeConfiguration = useCallback(async (
        gatewayUUID: string,
        source: string,
        said: string,
        alertUUID: string,
        noticeBody: any
    ) => {
        const noticeResponse: any = await updateSensorNotice(
            gatewayUUID,
            source,
            said,
            alertUUID,
            noticeBody
        );

        if (!isHttpSuccess(noticeResponse.status)) {
            showErrorAlert({
                title: noticeResponse.data?.title ?? "Notice Update Failed",
                message: noticeResponse.data?.description ?? "Failed to update notice configuration.",
            });
            return false;
        }
        return true;
    }, []);

    // Helper function to update local alert state as fallback
    const updateLocalAlertState = useCallback((
        newEnabledStatus: boolean,
        alertIndex: number,
        currentNotice: any
    ) => {
        const newAlertList = [...alertUUIDList];
        newAlertList[alertIndex] = {
            ...newAlertList[alertIndex],
            status: newEnabledStatus,
            notice: currentNotice ? {
                ...currentNotice,
                enabled_push: newEnabledStatus
            } : currentNotice
        };
        setAlertUUIDList(newAlertList);
    }, [alertUUIDList]);

    // Helper function to refresh alert data
    const refreshAlertData = useCallback(async (
        newEnabledStatus: boolean,
        alertIndex: number,
        currentNotice: any
    ) => {
        try {
            const refreshResponse = await getGatewayAlertNotification(id);
            if (isHttpSuccess(refreshResponse.status)) {
                const alerts = refreshResponse.data ?? [];
                const transformedAlerts = alerts.map((alert: any) => ({
                    uuid: alert.uuid,
                    source: alert.device_id,
                    said: alert.said,
                    gatewayUUID: alert.gateway_id,
                    status: alert.enabled,
                    notice: alert.notice,
                }));
                setAlertUUIDList(transformedAlerts);
            } else {
                updateLocalAlertState(newEnabledStatus, alertIndex, currentNotice);
            }
        } catch (refreshError) {
            console.error("Error refreshing alert data:", refreshError);
            updateLocalAlertState(newEnabledStatus, alertIndex, currentNotice);
        }
    }, [id, updateLocalAlertState]);

    // Custom updateAlertStatus that also updates notice configuration to sync with SensorDetails
    const updateAlertStatus = async (source: string, said: number) => {
        setIsAlertLoading(true);

        const alertInfo = findAlertInList(source, said);
        if (alertInfo?.status === undefined) {
            setIsAlertLoading(false);
            showErrorAlert({
                message: "Sensor is not yet configured.",
            });
            return;
        }

        const { index, uuid, status, gatewayUUID, notice } = alertInfo;
        const newEnabledStatus = !status;

        try {
            // Update notice configuration FIRST to ensure push notifications are enabled
            if (notice) {
                if (newEnabledStatus && !validateRecipientsList(notice.recipients_push ?? [])) {
                    setIsAlertLoading(false);
                    return;
                }
                const noticeBody = prepareNoticeBody(notice, newEnabledStatus);
                const noticeUpdated = await updateNoticeConfiguration(
                    gatewayUUID ?? "",
                    source,
                    String(said),
                    uuid,
                    noticeBody
                );
                if (!noticeUpdated) {
                    setIsAlertLoading(false);
                    return;
                }
            } else if (newEnabledStatus) {
                showErrorAlert({
                    message: "Alert notice configuration is missing. Please configure the alert in Sensor Details first.",
                });
                setIsAlertLoading(false);
                return;
            }

            // Update alert notification enabled status
            const updateResponse: any = await updateAlertNotification(
                gatewayUUID || "",
                source,
                String(said),
                uuid,
                { enabled: newEnabledStatus }
            );

            if (!isHttpSuccess(updateResponse.status)) {
                showErrorAlert({
                    title: updateResponse.data?.title,
                    message: updateResponse.data?.description ?? "Failed to update alert status.",
                });
                setIsAlertLoading(false);
                return;
            }

            // Refresh alert data from server
            await refreshAlertData(newEnabledStatus, index, notice);

            showSuccessAlert({
                message: `Alert has been ${status ? "disabled" : "enabled"
                }. It will take up to 2 minutes for the operation to take effect.`,
            });
        } catch (error) {
            console.error("Error updating alert status:", error);
            showErrorAlert({
                message: "An error occurred while updating the alert status.",
            });
        } finally {
            setIsAlertLoading(false);
        }
    };

    const returnCheckboxStatus = useCallback((source: string, said: number) => {
        return returnCheckboxStatusWithPush(alertUUIDList as any, source, said);
    }, [alertUUIDList]);

    const returnDisabledStatus = (source: string, said: number) => {
        return returnDisabledStatusWithPush(alertUUIDList as any, source, said);
    };

    // Function to fetch tags from API
    const fetchTags = useCallback(
        async (organizationId: string) => fetchTagsCommon(organizationId),
        []
    );

    // Helper function to extract UIDs from a single device
    const extractUIDsFromDevice = useCallback((device: any): string[] => {
        if (!Array.isArray(device) || device.length === 0) {
            return [];
        }
        const uidCandidates = [device[1], device[0]].filter(Boolean);
        return uidCandidates.map(String).filter(Boolean);
    }, []);

    // Helper function to extract UIDs from tag devices and add to a Set
    const extractUIDsFromTagDevices = useCallback((
        devices: any[],
        uids: Set<string>,
        gatewayId: string
    ): void => {
        for (const device of devices) {
            if (!Array.isArray(device) || String(device[0] ?? "") !== gatewayId) {
                continue;
            }
            const deviceUIDs = extractUIDsFromDevice(device);
            for (const uid of deviceUIDs) {
                uids.add(uid);
            }
        }
    }, [extractUIDsFromDevice]);

    // Function to extract UIDs from tags by type and create a map
    const extractTagUIDsByType = useCallback((tags: any[], gatewayId: string) => {
        const tagUIDsMap = new Map<string, Map<string, Set<string>>>();

        for (const type of ["zone", "room", "group", "hub"]) {
            const typeMap = new Map<string, Set<string>>();

            const filteredTags = tags.filter((tag: any) => tag.value?.startsWith(`${type}/`));
            for (const tag of filteredTags) {
                // Extract tag name from value (e.g., "zone/Zone 1" -> "Zone 1")
                const tagName = tag.value?.replace(`${type}/`, "") ?? "";
                const uids = new Set<string>();

                // Extract UIDs from devices array: [gatewayId, device_id, said]
                if (tag.devices && Array.isArray(tag.devices)) {
                    extractUIDsFromTagDevices(tag.devices, uids, gatewayId);
                }

                if (tagName && uids.size > 0) {
                    typeMap.set(tagName, uids);
                }
            }

            tagUIDsMap.set(type, typeMap);
        }

        return tagUIDsMap;
    }, [extractUIDsFromTagDevices]);

    // Helper function to extract UID/SAID mappings from tag devices
    const extractUIDSAIDsFromTagDevices = useCallback((
        devices: any[],
        gatewayId: string
    ): Map<string, Set<number>> => {
        const uidSaids = new Map<string, Set<number>>();
        for (const device of devices) {
            if (!Array.isArray(device) || String(device[0] ?? "") !== gatewayId) {
                continue;
            }
            addDeviceToUIDSAIDsMap(device, uidSaids);
        }
        return uidSaids;
    }, []);

    const extractTagUIDSAIDsByType = useCallback((tags: any[], gatewayId: string) => {
        const tagUIDSAIDsMap = new Map<string, Map<string, Map<string, Set<number>>>>();

        for (const type of ["zone", "room", "group", "hub"]) {
            const typeMap = new Map<string, Map<string, Set<number>>>();

            const filteredTags = tags.filter((tag: any) => tag.value?.startsWith(`${type}/`));
            for (const tag of filteredTags) {
                const tagName = tag.value?.replace(`${type}/`, "") ?? "";
                let uidSaids = new Map<string, Set<number>>();

                if (tag.devices && Array.isArray(tag.devices)) {
                    uidSaids = extractUIDSAIDsFromTagDevices(tag.devices, gatewayId);
                }

                if (tagName && uidSaids.size > 0) {
                    typeMap.set(tagName, uidSaids);
                }
            }

            tagUIDSAIDsMap.set(type, typeMap);
        }

        return tagUIDSAIDsMap;
    }, [extractUIDSAIDsFromTagDevices]);

    // Function to extract UIDs from registry devices
    const extractRegistryUIDs = useCallback((registryData: any): Set<string> => {
        const uids = new Set<string>();

        if (registryData?.info?.devices && Array.isArray(registryData.info.devices)) {
            for (const device of registryData.info.devices) {
                // Device can be an object with UID property or an array
                if (device?.UID) {
                    uids.add(String(device.UID));
                } else if (Array.isArray(device) && device.length > 0) {
                    uids.add(String(device[0]));
                } else if (typeof device === 'string') {
                    uids.add(device);
                }
            }
        }

        return uids;
    }, []);

    // Helper function to fetch heartbeat
    const fetchHeartbeat = async (gatewayId: string): Promise<number | null> => {
        try {
            const hbRes: any = await getGWReading();
            if (Array.isArray(hbRes?.data)) {
                const gatewayHeartbeat = hbRes.data.find((item: any) => item.gateway_id === gatewayId);
                return gatewayHeartbeat?.heartbeat ?? null;
            }
        } catch (error) {
            console.error("NetworkDetails: Error fetching heartbeat:", error);
        }
        return null;
    };

    // Helper function to check if a room tag is actually a zone entity (should be excluded)
    const isRoomTagActuallyZone = useCallback(
        (uids: Set<string>, devices: any[]): boolean =>
            isUIDSetZoneNotRoom(uids, devices),
        []
    );

    // Helper function to create an entity object from counts
    const createEntityFromCounts = useCallback(
        (
            tagName: string,
            counts: { sensors: number; controllers: number; rooms: number },
            includeRooms: boolean
        ): any => {
            const entity: any = {
                name: tagName,
                sensors: counts.sensors,
                controllers: counts.controllers,
                status: "Unknown",
            };

            if (includeRooms) {
                entity.rooms = counts.rooms;
            }

            return entity;
        },
        []
    );

    // Helper function to build entities (zones, rooms, groups) from tags
    const buildEntitiesFromTags = useCallback((
        tagType: string,
        extractedTagUIDsMap: Map<string, Map<string, Set<string>>>,
        extractedTagUIDSAIDsMap: Map<string, Map<string, Map<string, Set<number>>>>,
        registryUIDs: Set<string>,
        devices: any[],
        includeRooms: boolean = false
    ): any[] => {
        const entities: any[] = [];
        const tags = extractedTagUIDsMap.get(tagType) ?? new Map<string, Set<string>>();
        const tagUIDSAIDs = extractedTagUIDSAIDsMap.get(tagType) ?? new Map();
        const uidToDeviceMap = buildUIDToDeviceMap(devices);

        for (const [tagName, uids] of Array.from(tags.entries())) {
            const hasMatch = Array.from(uids).some((uid: string) => registryUIDs.has(uid));
            if (!hasMatch) continue;
            if (!hasMatchingRegistryNameOrNick(tagName, uids, registryUIDs, uidToDeviceMap)) {
                continue;
            }

            // For room entities, verify that the primary device is actually a room (not a zone)
            if (tagType === "room" && isRoomTagActuallyZone(uids, devices)) {
                continue;
            }

            const uidSaids = tagUIDSAIDs.get(tagName) ?? new Map();
            const counts = countDevicesByTagUIDSAIDs(uidSaids, devices);
            const entity = createEntityFromCounts(tagName, counts, includeRooms);
            entities.push(entity);
        }

        return entities;
    }, [isRoomTagActuallyZone, createEntityFromCounts]);

    // Helper function to build hubs from tags
    const buildHubsFromTags = (
        extractedTagUIDsMap: Map<string, Map<string, Set<string>>>,
        registryUIDs: Set<string>,
        registryDevices: any[]
    ): any[] => {
        const hubs: any[] = [];
        const hubTags = extractedTagUIDsMap.get("hub") ?? new Map<string, Set<string>>();

        const uidToDeviceMap = buildUIDToDeviceMap(registryDevices);

        for (const [tagName, uids] of Array.from(hubTags.entries())) {
            const allUIDs = Array.from(uids);

            // Only consider UIDs that exist in the registry.
            const matchingUIDs = allUIDs.filter((uid: string) => registryUIDs.has(uid));
            if (matchingUIDs.length === 0) continue;
            if (!hasMatchingRegistryNameOrNick(tagName, new Set<string>(matchingUIDs), registryUIDs, uidToDeviceMap)) {
                continue;
            }

            const selectedUID =
                selectEntityUUID(new Set<string>(matchingUIDs), registryDevices, tagName, "hub") ?? "";

            hubs.push({
                name: tagName,
                uuid: selectedUID,
                role: "Primary",
                lastActive: "Unknown"
            });
        }

        return hubs;
    };

    // Helper function to build network data object
    const buildNetworkData = (
        registryData: any,
        options: { id: string; primaryUID: string; heartbeat: number | null },
        entities: { zones: any[]; rooms: any[]; groups: any[]; hubs: any[] }
    ): any => {
        const timestamp = Math.round(Date.now() / 1000);
        const heartbeat = Number(options.heartbeat ?? 0);
        const isOnline = heartbeat > 0 && timestamp - heartbeat < HeartbeatUpdateAfter;
        return {
            name: registryData?.name ?? "Network",
            id: options.id,
            primaryHub: registryData?.info?.UUID ?? options.primaryUID ?? "-",
            heartbeat: options.heartbeat,
            info: !!registryData?.info,
            status: isOnline ? "Online" : "Offline",
            zones: entities.zones,
            rooms: entities.rooms,
            groups: entities.groups,
            sensors: [],
            controllers: [],
            auxiliaryHubs: [],
            hubs: entities.hubs,
        };
    };

    // Dedicated function to update only the network status (heartbeat)
    const updateNetworkStatus = useCallback(async () => {
        if (!id) {
            return;
        }

        setIsRefreshing(true);
        try {
            const heartbeat = await fetchHeartbeat(id);
            const timestamp = Math.round(Date.now() / 1000);
            const heartbeatValue = Number(heartbeat ?? 0);
            const isOnline = heartbeatValue > 0 && timestamp - heartbeatValue < HeartbeatUpdateAfter;
            setNetwork((prevNetwork: any) => {
                if (!prevNetwork) return prevNetwork;
                return {
                    ...prevNetwork,
                    heartbeat,
                    status: isOnline ? "Online" : "Offline",
                };
            });
        } catch (error) {
            console.error("NetworkDetails: Error updating network status:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id]);

    const toggleNetworkNameEdit = () => {
        setIsNetworkNameEditing(!isNetworkNameEditing);
    };

    const handleNetworkNameUpdate = async () => {
        if (!id || !networkName.trim()) {
            showErrorAlert({ message: "Network name cannot be empty." });
            return;
        }

        // Validate network name (min 1 char, max 32 chars, no special characters)
        const trimmedName = networkName.trim();
        if (trimmedName.length < 1 || trimmedName.length > 32) {
            showErrorAlert({ message: "Network name must be between 1 and 32 characters." });
            return;
        }

        if (!/^[a-zA-Z0-9\s]+$/.test(trimmedName)) {
            showErrorAlert({ message: "Network name can only include letters, numbers, and spaces." });
            return;
        }

        try {
            const response = await updateOneRegistry(id, { name: trimmedName });
            if (isHttpSuccess(response.status)) {
                setNetwork((prevNetwork: any) => {
                    if (!prevNetwork) return prevNetwork;
                    return {
                        ...prevNetwork,
                        name: trimmedName,
                    };
                });
                setIsNetworkNameEditing(false);
                showSuccessAlert({ message: "Network name updated successfully." });
            } else {
                showErrorAlert(getAPIError(response, "Failed to update network name."));
            }
        } catch (error) {
            console.error("NetworkDetails: Error updating network name:", error);
            showErrorAlert({ message: "Failed to update network name. Please try again." });
        }
    };

    // Fetch network data and status
    const fetchNetworkData = useCallback(async () => {
        if (!isValid || !currentOrgId) {
            setIsPageLoading(false);
            return;
        }

        try {
            const registryRes = await getOneRegistry(id);
            if (!isHttpSuccess(registryRes.status)) {
                setNetwork(null);
                setIsPageLoading(false);
                return;
            }

            const registryData = registryRes.data;
            const devices = registryData?.info?.devices ?? [];
            const primaryUID = registryData?.info?.UUID ?? registryData?.info?.uuid ?? id;

            const heartbeat = await fetchHeartbeat(id);
            const tags = await fetchTags(currentOrgId);
            const extractedTagUIDsMap = extractTagUIDsByType(tags, id);
            const extractedTagUIDSAIDsMap = extractTagUIDSAIDsByType(tags, id);
            tagUIDSAIDsMapRef.current = extractedTagUIDSAIDsMap;
            setTagUIDSAIDsMap(extractedTagUIDSAIDsMap);

            const registryUIDs = extractRegistryUIDs(registryData);

            const zones = buildEntitiesFromTags("zone", extractedTagUIDsMap, extractedTagUIDSAIDsMap, registryUIDs, devices, true);
            const rooms = buildEntitiesFromTags("room", extractedTagUIDsMap, extractedTagUIDSAIDsMap, registryUIDs, devices, false);
            const groups = buildEntitiesFromTags("group", extractedTagUIDsMap, extractedTagUIDSAIDsMap, registryUIDs, devices, false);
            const hubs = buildHubsFromTags(extractedTagUIDsMap, registryUIDs, devices);

            const networkData = buildNetworkData(registryData, { id, primaryUID, heartbeat }, { zones, rooms, groups, hubs });
            networkRef.current = networkData;
            setNetwork(networkData);
            setNetworkName(networkData?.name || "");
            setIsPageLoading(false);
        } catch (error) {
            console.error("NetworkDetails: Error fetching network data:", error);
            setNetwork(null);
            setIsPageLoading(false);
        }
    }, [isValid, id, currentOrgId, fetchTags, extractTagUIDsByType, extractTagUIDSAIDsByType, extractRegistryUIDs, buildEntitiesFromTags]);

    useEffect(() => {
        fetchNetworkData();
    }, [fetchNetworkData]);

    // Fetch alert data for sensors
    const fetchAlertData = useCallback(async () => {
        const ldsuGatewayId = id; // Use panlNetworkId from route params

        try {
            const alertResponse = await getGatewayAlertNotification(ldsuGatewayId);

            if (isHttpSuccess(alertResponse.status)) {
                const alerts = alertResponse.data ?? [];
                // Transform alert data to match sensor structure (similar to gateways)
                const transformedAlerts = alerts.map((alert: any) => ({
                    uuid: alert.uuid,
                    source: alert.device_id,
                    said: alert.said,
                    gatewayUUID: alert.gateway_id,
                    status: alert.enabled,
                    notice: alert.notice,
                }));
                setAlertUUIDList(transformedAlerts);
            }
        } catch (error) {
            console.error("NetworkDetails: Error fetching alert data:", error);
        }
    }, [id]);


    // Map readings to sensors (same logic as SensorList)
    const mapSensorsWithReadings = useCallback(async (sensors: any[], timeRange?: string, offset?: string) => {
        try {
            // Fetch readings for all buses (1, 2, 3)
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(
                    getAllLastBusReading(
                        id,
                        busNumber.toString(),
                        timeRange,
                        offset
                    )
                );
            }

            const results = await Promise.all(readingPromises);
            const allReadings: any[] = [];

            for (const result of results) {
                if (isHttpSuccess(result.status)) {
                    const { devices = [] } = result.data;
                    allReadings.push(...devices);
                }
            }

            // Map readings to sensors using best-candidate max/min per SAID
            const mappedSensors = sensors.map((sensor: any) => {
                const bestReading = pickBestSensorBusReadingForSAID(
                    sensor,
                    allReadings
                );

                if (bestReading) {
                    sensor.readings = {
                        highest: bestReading.highest,
                        lowest: bestReading.lowest,
                        timestamp: bestReading.timestamp,
                        value: bestReading.value,
                        minmax: [bestReading.highest, bestReading.lowest],
                    };
                }

                const raw = sensor.readings?.value;
                sensor.readingSortValue =
                    raw != null && raw !== "-" && !Number.isNaN(Number(raw))
                        ? Number(raw)
                        : Number.POSITIVE_INFINITY;
                const minmaxArr = sensor.readings?.minmax;
                sensor.minMaxSortValue =
                    minmaxArr && Array.isArray(minmaxArr) && minmaxArr.length >= 1
                        ? (() => {
                            const v = minmaxArr[0];
                            if (v == null || v === "-" || Number.isNaN(Number(v))) return Number.POSITIVE_INFINITY;
                            return Number(v);
                        })()
                        : Number.POSITIVE_INFINITY;
                return sensor;
            });
            return mappedSensors;
        } catch (error) {
            console.error("NetworkDetails: Error mapping sensor readings:", error);
            return sensors;
        }
    }, [id]);

    // Delete network handler
    const handleDeleteNetwork = async () => {
        if (!isValid) {
            showErrorAlert({
                message: "Invalid network ID.",
            });
            setModalShow(false);
            return;
        }

        setModalShow(false);

        try {
            const response = await deleteRegistry(id);

            if (isHttpSuccess(response.status)) {
                // Clean up location popup if needed
                addOrRemoveConfirmLocationPopup(id, false);

                showSuccessAlert({
                    message: "Network has been deleted successfully.",
                });

                // Navigate back to networks list
                history.push('/networks');
            } else {
                showErrorAlert(getAPIError(response, ACTION_FAIL_MESSAGE));
            }
        } catch (error) {
            console.error("NetworkDetails: Error deleting network:", error);
            showErrorAlert({
                message: "An error occurred while deleting the network. Please try again later.",
            });
        }
    };

    // Scan network to discover new devices
    // After scanning, this will:
    // 1. Refresh registry data (new devices discovered)
    // 2. Refresh tags API (zones, rooms, groups, hubs relationships)
    // 3. Rebuild zones, rooms, groups, and hubs with updated counts
    // 4. Query statuses for zones, rooms, groups, and hubs
    const handleScanSuccess = async () => {
        showSuccessAlert({
            message: SCAN_REQUEST_MESSAGE,
        });
        await fetchNetworkData();
        await fetchLdsuData();
    };

    const handleEmptyConfig = async (): Promise<boolean> => {
        const registryResetRes = await resetGateway(id);
        if (isHttpSuccess(registryResetRes.status)) {
            await handleScanSuccess();
            return true;
        } else {
            showErrorAlert(
                getAPIError(
                    registryResetRes,
                    SCAN_GATEWAY_FAIL_MESSAGE
                )
            );
            return false;
        }
    };

    const processRegistryConfig = async (): Promise<boolean> => {
        const registryConfigRes = await getRegistryConfig(id);
        if (!isHttpSuccess(registryConfigRes.status)) {
            showErrorAlert(
                getAPIError(registryConfigRes, SCAN_GATEWAY_FAIL_MESSAGE)
            );
            return false;
        }

        const registryConfigData = registryConfigRes?.data ?? [];
        if (registryConfigData.length === 0) {
            return await handleEmptyConfig();
        } else {
            await handleScanSuccess();
            return true;
        }
    };

    // 5. Refresh sensors, actuators, and auxiliary hubs
    const handleScanNetwork = async () => {
        if (!isValid) {
            showErrorAlert({
                message: "Invalid network ID.",
            });
            return;
        }

        setIsScanning(true);
        try {
            // Scan the network (bus 0 for gateway scan)
            const res = await scanGateway(id, 0, false);

            if (isHttpSuccess(res.status)) {
                await processRegistryConfig();
            } else {
                // Adjust backend message to use "Network" instead of "Gateway" for this page
                const adjustedError: any = { ...res };
                if (adjustedError?.data?.description) {
                    adjustedError.data = {
                        ...adjustedError.data,
                        description: adjustedError.data.description.replace(/^Gateway\b/, "Network"),
                    };
                }
                showErrorAlert(getAPIError(adjustedError, ACTION_FAIL_MESSAGE));
            }
        } catch (error) {
            console.error("NetworkDetails: Error scanning network:", error);
            showErrorAlert({
                message: "An error occurred while scanning the network. Please try again later.",
            });
        } finally {
            setIsScanning(false);
        }
    };

    // Refresh all sensor readings 
    const refreshAllSensorReadings = useCallback(async (customTimeRange?: string, customOffset?: string) => {
        if (!id || !rawSensorList.length) return;

        setIsRefreshing(true);
        try {
            const sensorsWithReadings = await mapSensorsWithReadings(
                rawSensorList,
                customTimeRange,
                customOffset
            );
            setRawSensorList(sensorsWithReadings);

            // Update network state with refreshed sensor readings
            setNetwork((prevNetwork: any) => {
                if (!prevNetwork) return prevNetwork;
                return {
                    ...prevNetwork,
                    sensors: sensorsWithReadings
                };
            });
        } catch (error) {
            console.error("NetworkDetails: Error refreshing sensor readings:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, rawSensorList, mapSensorsWithReadings]);

    // Refresh a single sensor's reading using getSensorLastReading (similar to gateway implementation)
    const refreshOneSensor = async (ldsuUUID: string, SAID: number) => {
        if (!id || !ldsuUUID) return;

        try {
            const { lastTime, offsetTime } = getPrometheusTimeRangeForSensorReadings(timeRange);
            const { data, status } = await getSensorLastReading(
                id,
                ldsuUUID,
                lastTime,
                offsetTime
            );
            if (isHttpSuccess(status)) {
                const rawValueCandidateRaw = get(data.value, `[${SAID}]`, "-");
                const highestCandidateRaw = get(data.max, `[${SAID}]`, "-");
                const lowestCandidateRaw = get(data.min, `[${SAID}]`, "-");

                const rawValueCandidate = rawValueCandidateRaw ?? "-";
                const highestCandidate = highestCandidateRaw ?? "-";
                const lowestCandidate = lowestCandidateRaw ?? "-";

                // Update the sensor in rawSensorList
                setRawSensorList((prevSensors: any[]) => {
                    return prevSensors.map((s: any) => {
                        if (String(s.ldsu_uuid) === String(ldsuUUID) && String(s.SAID) === String(SAID)) {
                            const prevReadings = s.readings ?? {};

                            const updatedReading = mergeSensorLastReadingWithMinMaxFallback(
                                prevReadings,
                                {
                                    highest: highestCandidate,
                                    lowest: lowestCandidate,
                                    value: rawValueCandidate,
                                    timestamp: data?.ts,
                                }
                            );

                            const readingSortValue =
                                updatedReading.value != null &&
                                updatedReading.value !== "-" &&
                                !Number.isNaN(Number(updatedReading.value))
                                    ? Number(updatedReading.value)
                                    : Number.POSITIVE_INFINITY;

                            const minMaxCandidate = updatedReading.minmax?.[0];
                            const minMaxSortValue =
                                minMaxCandidate != null &&
                                minMaxCandidate !== "-" &&
                                !Number.isNaN(Number(minMaxCandidate))
                                    ? Number(minMaxCandidate)
                                    : Number.POSITIVE_INFINITY;

                            return { ...s, readings: updatedReading, readingSortValue, minMaxSortValue };
                        }
                        return s;
                    });
                });

                // Update the sensor in network state
                setNetwork((prevNetwork: any) => {
                    if (!prevNetwork) return prevNetwork;
                    return {
                        ...prevNetwork,
                        sensors: prevNetwork.sensors?.map((s: any) => {
                            if (String(s.ldsu_uuid) === String(ldsuUUID) && String(s.SAID) === String(SAID)) {
                                const prevReadings = s.readings ?? {};

                                const updatedReading = mergeSensorLastReadingWithMinMaxFallback(
                                    prevReadings,
                                    {
                                        highest: highestCandidate,
                                        lowest: lowestCandidate,
                                        value: rawValueCandidate,
                                        timestamp: data?.ts,
                                    }
                                );

                                const readingSortValue =
                                    updatedReading.value != null &&
                                    updatedReading.value !== "-" &&
                                    !Number.isNaN(Number(updatedReading.value))
                                        ? Number(updatedReading.value)
                                        : Number.POSITIVE_INFINITY;

                                const minMaxCandidate = updatedReading.minmax?.[0];
                                const minMaxSortValue =
                                    minMaxCandidate != null &&
                                    minMaxCandidate !== "-" &&
                                    !Number.isNaN(Number(minMaxCandidate))
                                        ? Number(minMaxCandidate)
                                        : Number.POSITIVE_INFINITY;

                                return { ...s, readings: updatedReading, readingSortValue, minMaxSortValue };
                            }
                            return s;
                        }) || []
                    };
                });
            }
        } catch (error) {
            console.error("NetworkDetails: Error refreshing sensor reading:", error);
        }
    };

    // Handle refresh sensor click (similar to gateway implementation)
    const handleRefreshSensor = async (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        sensor: any
    ) => {
        // Store element reference before async operation to avoid null reference
        const target = e.currentTarget as HTMLElement;
        if (!target) return;

        // Create unique identifier for this sensor
        const sensorId = `${sensor.ldsu_uuid}-${sensor.SAID}`;

        // Add data attribute to find element later
        target.dataset.sensorId = sensorId;

        // Add refresh animation class
        target.classList.add("refresh-state");

        try {
            await refreshOneSensor(sensor.ldsu_uuid, sensor.SAID);
        } finally {
            // Find element again after async operation (in case DOM was updated)
            const element = document.querySelector(`.refresh-sensor[data-sensor-id="${sensorId}"]`) as HTMLImageElement;
            if (element) {
                element.classList.remove("refresh-state");
                delete element.dataset.sensorId;
            }
        }
    };

    // Single-row actuator refresh: per-device reading2 (getSensorLastReading). Bulk list still uses getAllLastBusReading.
    const refreshOneActuator = useCallback(
        async (actuatorUID: string, said: number) => {
            if (!id || !actuatorUID) return;

            try {
                const { data, status } = await getSensorLastReading(id, actuatorUID);
                if (!isHttpSuccess(status)) return;

                const parsed = getControllerStatusFromReading2(data, said);
                if (!parsed) return;

                const { status: updatedStatus, timestamp: updatedTimestamp } = parsed;

                setRawActuatorList((prevActuators: any[]) =>
                    prevActuators.map((actuator: any) => {
                        if (actuator.UID === actuatorUID && actuator.said === said) {
                            return {
                                ...actuator,
                                readings: {
                                    value: updatedStatus,
                                    status: updatedStatus,
                                    timestamp: updatedTimestamp,
                                },
                                timestamp: updatedTimestamp,
                            };
                        }
                        return actuator;
                    })
                );
            } catch (error) {
                console.error("NetworkDetails: Error refreshing actuator status:", error);
            }
        },
        [id]
    );

    // Handle refresh actuator/controller click for a single row
    const handleRefreshActuator = async (
        e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
        row: any
    ) => {
        // Store element reference before async operation to avoid null reference
        const target = e.currentTarget as HTMLElement;
        if (!target || !row?.UID || row.said == null) return;

        // Add refresh animation class
        target.classList.add("refresh-state");

        try {
            await refreshOneActuator(row.UID, row.said);
        } finally {
            // Remove refresh animation class
            target.classList.remove("refresh-state");
        }
    };

    // Map readings to actuators (similar to sensors but for actuator data)
    const mapActuatorsWithReadings = useCallback(async (actuators: any[], timeRange?: string, offset?: string) => {
        try {
            // Fetch readings for all buses (1, 2, 3)
            const readingPromises = [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                readingPromises.push(
                    getAllLastBusReading(
                        id,
                        busNumber.toString(),
                        timeRange,
                        offset
                    )
                );
            }

            const results = await Promise.all(readingPromises);
            const allReadings: any[] = [];

            for (const result of results) {
                if (isHttpSuccess(result.status)) {
                    const { devices = [] } = result.data;
                    allReadings.push(...devices);
                }
            }

            // Map readings to actuators
            const mappedActuators = actuators.map((actuator: any) => {
                for (const reading of allReadings) {
                    if (actuator.UID === reading.UID) {
                        // Get the specific actuator value using the said index
                        const actuatorValue = get(reading.value, `[${actuator.said}]`, null);
                        const actuatorStatus = get(reading.value, `[${actuator.said}]`, null);

                        actuator.readings = {
                            value: actuatorValue,
                            status: actuatorStatus,
                            timestamp: reading.ts,
                        };
                    }
                }
                return actuator;
            });
            return mappedActuators;
        } catch (error) {
            console.error("NetworkDetails: Error mapping actuator readings:", error);
            return actuators;
        }
    }, [id]);

    // Refresh all actuator readings using time range (similar to sensors)
    const refreshAllActuatorReadings = useCallback(async (customTimeRange?: string, customOffset?: string) => {
        if (!id || !rawActuatorList.length) return;

        setIsRefreshing(true);
        try {
            const actuatorsWithReadings = await mapActuatorsWithReadings(
                rawActuatorList,
                customTimeRange,
                customOffset
            );
            setRawActuatorList(actuatorsWithReadings);

            // Update network state with refreshed actuator readings
            setNetwork((prevNetwork: any) => {
                if (!prevNetwork) return prevNetwork;
                return {
                    ...prevNetwork,
                    controllers: actuatorsWithReadings.map(actuator => ({
                        name: actuator.name,
                        type: actuator.type,
                        status: actuator.status,
                        info: actuator.info,
                        mode: actuator.info?.mode ?? '-',
                        UID: actuator.UID,
                        said: actuator.said,
                        timestamp: actuator.timestamp
                    })),
                };
            });
        } catch (error) {
            console.error("NetworkDetails: Error refreshing actuator readings:", error);
        } finally {
            setIsRefreshing(false);
        }
    }, [id, rawActuatorList, mapActuatorsWithReadings]);

    const updateNetworkWithLdsuData = useCallback(async (
        registryInfoRes: any,
        sensorsWithReadings: any[],
        actuatorsWithReadings: any[],
        allAuxiliaryHubs: any[]
    ) => {
        const devices = registryInfoRes.data?.info?.devices ?? [];

        // Query statuses for zones, rooms, groups, and hubs
        // First, get the current network state
        const currentNetwork = networkRef.current;

        if (!currentNetwork) {
            // If network is not available yet, just update sensors/controllers and return
            setNetwork((prevNetwork: any) => {
                if (!prevNetwork) return prevNetwork;
                const updatedNetwork = { ...prevNetwork };
                updatedNetwork.sensors = sensorsWithReadings;
                updatedNetwork.controllers = actuatorsWithReadings.map(actuator => ({
                    name: actuator.name,
                    type: actuator.type,
                    status: actuator.status,
                    info: actuator.info,
                    mode: actuator.info?.mode ?? '-',
                    UID: actuator.UID,
                    said: actuator.said,
                    timestamp: actuator.timestamp
                }));
                updatedNetwork.auxiliaryHubs = allAuxiliaryHubs;
                networkRef.current = updatedNetwork;
                return updatedNetwork;
            });
            return;
        }

        const updatedNetwork = { ...currentNetwork };

        // Build UID→device map once for zone/room/group instance counts
        const uidToDeviceMap = buildUIDToDeviceMap(devices);

        // Collect all status queries and update state once at the end to prevent infinite loops
        const statusUpdatePromises: Promise<{ type: string; data: any[] }>[] = [];

        // Update zones with counts and status (use instance counts so VIRTUAL/aux-hub sensors are included)
        if (Array.isArray(updatedNetwork.zones) && updatedNetwork.zones.length > 0) {
            const zoneTagUIDSAIDs = tagUIDSAIDsMap.get("zone") || new Map();
            const zonesPromise = Promise.all(
                updatedNetwork.zones.map(async (zone: any) => {
                    const uidSaids = zoneTagUIDSAIDs.get(zone.name) || new Map<string, Set<number>>();
                    const zoneUIDs = new Set<string>(uidSaids.keys());
                    const zoneUuid = selectEntityUUID(zoneUIDs, devices, zone.name, "zone");
                    const zoneUIDSAIDsForStatus = selectUIDSAIDsForUID(uidSaids, zoneUuid);
                    const instanceCounts = countSensorActuatorInstancesByTag(
                        sensorsWithReadings,
                        actuatorsWithReadings,
                        zoneUIDs,
                        uidSaids,
                        uidToDeviceMap
                    );
                    const roomCounts = countDevicesByTagUIDSAIDs(uidSaids, devices);

                    // Query zone status
                    let zoneStatus = "Unknown";
                    if (currentOrgId && id && zone.name) {
                        try {
                            zoneStatus = await queryTagStatusFromUIDSAIDs(id, zoneUIDSAIDsForStatus);
                        } catch (error) {
                            console.error("NetworkDetails: Error querying zone status for", zone.name, ":", error);
                        }
                    }

                    return {
                        ...zone,
                        uuid: zoneUuid ?? undefined,
                        sensors: instanceCounts.sensors,
                        controllers: instanceCounts.controllers,
                        rooms: roomCounts.rooms,
                        status: zoneStatus
                    };
                })
            ).then((zonesWithStatus) => ({ type: 'zones', data: zonesWithStatus }))
                .catch((error) => {
                    console.error("NetworkDetails: Error in zones status promise:", error);
                    return { type: 'zones', data: updatedNetwork.zones ?? [] };
                });
            statusUpdatePromises.push(zonesPromise);
        }

        // Update rooms with counts and status (use instance counts so table matches Room Details page)
        if (Array.isArray(updatedNetwork.rooms) && updatedNetwork.rooms.length > 0) {
            const roomTagUIDSAIDs = tagUIDSAIDsMap.get("room") ?? new Map();
            const roomsPromise = Promise.all(
                updatedNetwork.rooms.map(async (room: any) => {
                    const uidSaids = roomTagUIDSAIDs.get(room.name) || new Map<string, Set<number>>();
                    const roomUIDs = new Set<string>(uidSaids.keys());
                    const roomUuid = selectEntityUUID(roomUIDs, devices, room.name, "room");
                    const roomUIDSAIDsForStatus = selectUIDSAIDsForUID(uidSaids, roomUuid);
                    const counts = countSensorActuatorInstancesByTag(
                        sensorsWithReadings,
                        actuatorsWithReadings,
                        roomUIDs,
                        uidSaids,
                        uidToDeviceMap
                    );

                    // Query room status
                    let roomStatus = "Unknown";
                    if (currentOrgId && id && room.name) {
                        try {
                            roomStatus = await queryTagStatusFromUIDSAIDs(id, roomUIDSAIDsForStatus);
                        } catch (error) {
                            console.error("NetworkDetails: Error querying room status for", room.name, ":", error);
                        }
                    }

                    return {
                        ...room,
                        uuid: roomUuid ?? undefined,
                        sensors: counts.sensors,
                        controllers: counts.controllers,
                        status: roomStatus
                    };
                })
            ).then((roomsWithStatus) => ({ type: 'rooms', data: roomsWithStatus }))
                .catch((error) => {
                    console.error("NetworkDetails: Error in rooms status promise:", error);
                    return { type: 'rooms', data: updatedNetwork.rooms ?? [] };
                });
            statusUpdatePromises.push(roomsPromise);
        }

        // Update groups with counts and status (use instance counts so table matches Group Details page)
        if (Array.isArray(updatedNetwork.groups) && updatedNetwork.groups.length > 0) {
            const groupTagUIDSAIDs = tagUIDSAIDsMap.get("group") || new Map();
            const groupsPromise = Promise.all(
                updatedNetwork.groups.map(async (group: any) => {
                    const uidSaids = groupTagUIDSAIDs.get(group.name) || new Map<string, Set<number>>();
                    const groupUIDs = new Set<string>(uidSaids.keys());
                    const groupUuid = selectEntityUUID(groupUIDs, devices, group.name, "group");
                    const groupUIDSAIDsForStatus = selectUIDSAIDsForUID(uidSaids, groupUuid);
                    const counts = countSensorActuatorInstancesByTag(
                        sensorsWithReadings,
                        actuatorsWithReadings,
                        groupUIDs,
                        uidSaids,
                        uidToDeviceMap
                    );

                    // Query group status
                    let groupStatus = "Unknown";
                    if (currentOrgId && id && group.name) {
                        try {
                            groupStatus = await queryTagStatusFromUIDSAIDs(id, groupUIDSAIDsForStatus);
                        } catch (error) {
                            console.error("NetworkDetails: Error querying group status for", group.name, ":", error);
                        }
                    }

                    return {
                        ...group,
                        uuid: groupUuid ?? undefined,
                        sensors: counts.sensors,
                        controllers: counts.controllers,
                        status: groupStatus
                    };
                })
            ).then((groupsWithStatus) => ({ type: 'groups', data: groupsWithStatus }))
                .catch((error) => {
                    console.error("NetworkDetails: Error in groups status promise:", error);
                    return { type: 'groups', data: updatedNetwork.groups ?? [] };
                });
            statusUpdatePromises.push(groupsPromise);
        }

        // Update hubs with status
        if (Array.isArray(updatedNetwork.hubs) && updatedNetwork.hubs.length > 0) {
            const hubsPromise = Promise.all(
                updatedNetwork.hubs.map(async (hub: any) => {
                    const hubTagUIDSAIDs =
                        tagUIDSAIDsMap.get("hub") || new Map<string, Map<string, Set<number>>>();
                    const uidSaids =
                        hubTagUIDSAIDs.get(hub.name) || new Map<string, Set<number>>();
                    const hubUIDs = new Set<string>(uidSaids.keys());
                    const hubUuid = selectEntityUUID(hubUIDs, devices, hub.name, "hub");
                    const hubUIDSAIDsForStatus = selectUIDSAIDsForUID(uidSaids, hubUuid);

                    // Query hub status
                    let hubStatus = "Unknown";
                    if (currentOrgId && id && hub.name) {
                        try {
                            const statusResult = await queryTagStatusFromUIDSAIDs(id, hubUIDSAIDsForStatus);
                            // Convert "On"/"Off" to "Online"/"Offline" for hub display
                            if (statusResult === "On") {
                                hubStatus = "Online";
                            } else if (statusResult === "Off") {
                                hubStatus = "Offline";
                            }
                            // If statusResult is neither "On" nor "Off", hubStatus remains "Unknown" (initialized value)
                        } catch (error) {
                            console.error(
                                "NetworkDetails: Error querying hub status for",
                                hub.name,
                                ":",
                                error
                            );
                        }
                    }

                    return {
                        ...hub,
                        uuid: hubUuid ?? undefined,
                        status: hubStatus
                    };
                })
            )
                .then((hubsWithStatus) => ({ type: "hubs", data: hubsWithStatus }))
                .catch((error) => {
                    console.error("NetworkDetails: Error in hubs status promise:", error);
                    return { type: "hubs", data: updatedNetwork.hubs ?? [] };
                });
            statusUpdatePromises.push(hubsPromise);
        }

        // Wait for all status queries to complete, then update state once to prevent infinite loops
        const statusResults = await Promise.all(statusUpdatePromises);

        // Update network state once with all status results
        setNetwork((prevNetwork: any) => {
            if (!prevNetwork) return prevNetwork;
            const updated = { ...prevNetwork };

            // Update sensors and controllers arrays
            updated.sensors = sensorsWithReadings;
            updated.controllers = actuatorsWithReadings.map(actuator => ({
                name: actuator.name,
                type: actuator.type,
                status: actuator.status,
                info: actuator.info,
                mode: actuator.info?.mode ?? '-',
                UID: actuator.UID,
                said: actuator.said,
                timestamp: actuator.timestamp
            }));
            updated.auxiliaryHubs = allAuxiliaryHubs;

            // Update statuses from query results
            for (const result of statusResults) {
                if (result?.type && result?.data) {
                    updated[result.type] = result.data;
                }
            }

            networkRef.current = updated;
            return updated;
        });

        // Fetch alert data for sensors
        await fetchAlertData();
    }, [tagUIDSAIDsMap, currentOrgId, id, fetchAlertData]);

    // Fetch LDSU data and transform to auxiliary hub format
    const fetchLdsuData = useCallback(async () => {
        const ldsuGatewayId = id; // Use panlNetworkId from route params

        try {
            // Fetch LDSU data using gateway service functions with the specific gateway ID
            const [registryInfoRes, registryConfigRes, registryGwAndLdsuStatusRes] = await Promise.all([
                getOneRegistry(ldsuGatewayId),
                getRegistryConfig(ldsuGatewayId),
                getGwAndLDSUStatus(ldsuGatewayId)
            ]);

            if ([registryInfoRes.status, registryConfigRes.status, registryGwAndLdsuStatusRes.status].includes(HttpStatus.FORBIDDEN)) {
                return;
            }

            if (!isHttpSuccess(registryInfoRes.status) || !isHttpSuccess(registryConfigRes.status) || !isHttpSuccess(registryGwAndLdsuStatusRes.status)) {
                return;
            }

            // Get all LDSUs from all buses and transform them
            const allAuxiliaryHubs: any[] = [];

            // Process each bus (assuming 3 buses as in the original code)
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                const auxiliaryHubs = getAuxiliaryHubsFromLdsus(
                    busNumber.toString(),
                    registryInfoRes.data?.info?.devices ?? [],
                    registryConfigRes.data ?? [],
                    registryGwAndLdsuStatusRes.data?.devices ?? []
                );
                allAuxiliaryHubs.push(...auxiliaryHubs);
            }

            setLdsuAuxiliaryHubs(allAuxiliaryHubs);

            // Get all sensors from all buses and transform them
            const allRawSensors: any[] = [];
            const allRawActuators: any[] = [];
            const actuatorKeysSeen = new Set<string>();

            const addActuatorOnce = (a: any) => {
                const key = `${String(a?.UID)}:${Number(a?.said)}`;
                if (actuatorKeysSeen.has(key)) return;
                actuatorKeysSeen.add(key);
                allRawActuators.push(a);
            };

            // Process each bus (assuming 3 buses as in the original code)
            const registryDevices = registryInfoRes.data?.info?.devices ?? [];
            for (let busNumber = 1; busNumber <= 3; busNumber++) {
                // Get raw sensor data using getLDSBusInfo (same as LDSBusList / gateways)
                const { sensors: rawSensors, actuators: rawActuators } = getLDSBusInfo(
                    busNumber.toString(),
                    registryDevices,
                    registryConfigRes.data ?? [],
                    registryGwAndLdsuStatusRes.data?.devices ?? []
                );

                // Filter out sensors from zone/room/group/hub entity devices (e.g. "Binary Digital Input")
                const actualSensors = rawSensors.filter(
                    (s: any) => !isSensorFromEntityDevice(s, registryDevices)
                );
                // At network level, trust per‑channel CLS/type from getLDSBusInfo /
                // getActuatorList and do not re‑filter by device OBJ. This avoids
                // accidentally removing valid actuator channels on auxiliary hubs
                // (e.g. LDSBus 2CH Relay with OBJ 16385).
                const filteredActuators = rawActuators;

                // Include IO controller output channels that have CMDS in config but no APP from registry
                const ioControllerActuators = getIOControllerActuatorsFromConfig(
                    busNumber.toString(),
                    registryDevices,
                    registryConfigRes.data ?? [],
                    registryGwAndLdsuStatusRes.data?.devices ?? []
                );

                allRawSensors.push(...actualSensors);
                filteredActuators.forEach(addActuatorOnce);
                ioControllerActuators.forEach(addActuatorOnce);
            }

            // Compute default time range for initial load (match current timeRange state)
            let lastTimeValue = Math.round(
                moment.duration(timeRange.endDate.diff(timeRange.startDate)).asSeconds()
            );
            let offsetTime: string | undefined;

            if (timeRange.range === CHART_CUSTOM_RANGE_OPTION) {
                lastTimeValue = Math.round(
                    moment.duration(moment().diff(timeRange.endDate)).asSeconds()
                );

                offsetTime = convertSecondsToPrometheusTime(lastTimeValue);
                lastTimeValue = moment
                    .duration(timeRange.endDate.diff(timeRange.startDate))
                    .asSeconds();
            }

            const lastTime = convertSecondsToPrometheusTime(lastTimeValue);

            // Fetch readings data and map to sensors and actuators using current time range
            const [sensorsWithReadings, actuatorsWithReadings] = await Promise.all([
                mapSensorsWithReadings(allRawSensors, lastTime, offsetTime),
                mapActuatorsWithReadings(allRawActuators, lastTime, offsetTime)
            ]);

            // Store sensor and actuator data with readings
            setRawSensorList(sensorsWithReadings);
            setRawActuatorList(actuatorsWithReadings);

            await updateNetworkWithLdsuData(
                registryInfoRes,
                sensorsWithReadings,
                actuatorsWithReadings,
                allAuxiliaryHubs
            );
        } catch (error) {
            console.error("NetworkDetails: Error fetching LDSU data:", error);
        }
    }, [id, timeRange, mapSensorsWithReadings, mapActuatorsWithReadings, updateNetworkWithLdsuData]);

    const mapEntityQueriedStatus = useCallback(
        (tabKey: EntityTabKey, queriedStatus: string): string => {
            if (tabKey !== "hubs") {
                return queriedStatus;
            }

            if (queriedStatus === "On") {
                return "Online";
            }
            if (queriedStatus === "Off") {
                return "Offline";
            }
            return "Unknown";
        },
        []
    );

    const getLatestTagMapForTab = useCallback(
        async (
            tabKey: EntityTabKey,
            rows: any[]
        ): Promise<Map<string, Map<string, Set<number>>>> => {
            const tabToTagType: Record<EntityTabKey, EntityTagType> = {
                zones: "zone",
                rooms: "room",
                groups: "group",
                hubs: "hub",
            };
            const tagType = tabToTagType[tabKey];
            const expectedTagNames: Set<string> = new Set<string>(
                rows
                    .map((row: any) => String(row?.name ?? "").trim())
                    .filter(Boolean)
            );

            let activeTagUIDSAIDsMap = tagUIDSAIDsMapRef.current;
            for (let attempt = 0; attempt < 3; attempt++) {
                try {
                    const latestTags = await fetchTags(currentOrgId);
                    const latestTagUIDSAIDsMap = extractTagUIDSAIDsByType(latestTags, id);
                    activeTagUIDSAIDsMap = latestTagUIDSAIDsMap;
                    tagUIDSAIDsMapRef.current = latestTagUIDSAIDsMap;
                    setTagUIDSAIDsMap(latestTagUIDSAIDsMap);
                } catch (error) {
                    console.error("NetworkDetails: Failed to fetch latest tags for entity status refresh:", error);
                }

                const currentTagMap = activeTagUIDSAIDsMap.get(tagType) ?? new Map<string, Map<string, Set<number>>>();
                const hasAllExpectedTags = Array.from(expectedTagNames).every((name: string) => currentTagMap.has(name));
                if (hasAllExpectedTags || attempt === 2) {
                    return currentTagMap;
                }
                await new Promise((resolve) => setTimeout(resolve, 800));
            }

            return new Map<string, Map<string, Set<number>>>();
        },
        [currentOrgId, id, fetchTags, extractTagUIDSAIDsByType]
    );

    const refreshAllEntityStatuses = async (
        tabKey: EntityTabKey
    ) => {
        if (!id || !currentOrgId) return;
        setIsEntityStatusRefreshing(true);
        try {
            const currentNetwork = network;
            if (!currentNetwork) {
                return;
            }
            const rows = Array.isArray(currentNetwork[tabKey]) ? currentNetwork[tabKey] : [];
            if (rows.length === 0) {
                return;
            }
            const tagMap = await getLatestTagMapForTab(tabKey, rows);
            const updatedRows = await Promise.all(
                rows.map(async (row: any) => {
                    try {
                        const uidSaids = tagMap.get(row.name) ?? new Map<string, Set<number>>();
                        const uidSaidsForStatus = selectUIDSAIDsForUID(uidSaids, row.uuid);
                        const queriedStatus = await queryTagStatusFromUIDSAIDs(id, uidSaidsForStatus);
                        const mappedStatus = mapEntityQueriedStatus(tabKey, queriedStatus);
                        return { ...row, status: mappedStatus };
                    } catch (error) {
                        console.error(`NetworkDetails: Error refreshing ${tabKey} status for`, row?.name, ":", error);
                        return row;
                    }
                })
            );
            const statusByEntityKey = new Map<string, string>();
            for (const row of updatedRows) {
                const rowName = String(row?.name ?? "");
                const rowUuid = String(row?.uuid ?? "");
                statusByEntityKey.set(`${rowName}::${rowUuid}`, String(row?.status ?? "Unknown"));
                // Fallback key for rows without uuid (or when uuid changed across refresh)
                if (!rowUuid) {
                    statusByEntityKey.set(rowName, String(row?.status ?? "Unknown"));
                }
            }

            setNetwork((prevNetwork: any) => {
                if (!prevNetwork) return prevNetwork;
                const liveRows = Array.isArray(prevNetwork[tabKey]) ? prevNetwork[tabKey] : [];
                return {
                    ...prevNetwork,
                    [tabKey]: liveRows.map((row: any) => {
                        const rowName = String(row?.name ?? "");
                        const rowUuid = String(row?.uuid ?? "");
                        const nextStatus =
                            statusByEntityKey.get(`${rowName}::${rowUuid}`) ??
                            statusByEntityKey.get(rowName);
                        return nextStatus ? { ...row, status: nextStatus } : row;
                    }),
                };
            });
        } catch (error) {
            console.error("NetworkDetails: Error refreshing entity statuses:", error);
        } finally {
            setIsEntityStatusRefreshing(false);
        }
    };

    // Fetch LDSU data when component mounts and network is available
    // Use a ref to prevent infinite loops from network state updates
    const hasFetchedLdsuData = React.useRef(false);

    useEffect(() => {
        if (id && id !== 'undefined' && network && !hasFetchedLdsuData.current) {
            hasFetchedLdsuData.current = true;
            // Only fetch LDSU data once per mount/network load.
            // Resetting this flag with a timer can cause repeated in-flight fetches and flakiness
            // when responses arrive out of order.
            fetchLdsuData().finally(() => undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, network]); // Fetch when id or network changes

    // Reset the flag when id changes
    useEffect(() => {
        hasFetchedLdsuData.current = false;
    }, [id]);

    // Refresh alert data when sensors tab becomes active to ensure we have latest data
    // This ensures consistency when navigating back from SensorDetails
    useEffect(() => {
        if (activeTab === 'sensors' && id && id !== 'undefined') {
            fetchAlertData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, id]);

    // Tab detection logic
    const getTabType = (tabKey: string, data: any[]) => {
        if (!data || data.length === 0) return false;

        const firstRow = data[0] || {};
        switch (tabKey) {
            case 'sensors':
                // Sensor sample: sensor_name, sensor_type, readings object
                return typeof firstRow.sensor_name === 'string' &&
                    typeof firstRow.sensor_type === 'string' &&
                    typeof firstRow.readings === 'object';
            case 'controllers':
                // Actuator sample: UID, said and either info object or status array
                return !!firstRow.UID && (firstRow.said !== undefined) &&
                    (typeof firstRow.info === 'object' || Array.isArray(firstRow.status));
            case 'hubs':
                return typeof firstRow.name === 'string' &&
                    typeof firstRow.uuid === 'string' &&
                    typeof firstRow.role === 'string' &&
                    !('reading' in firstRow) &&
                    ('lastActive' in firstRow || 'lastactive' in firstRow);
            case 'auxiliaryHubs':
                // Auxiliary hub sample: uuid string and status string
                return typeof firstRow.uuid === 'string' && typeof firstRow.status === 'string';
            case 'zones':
                return typeof firstRow.name === 'string' &&
                    typeof firstRow.sensors === 'number' &&
                    typeof firstRow.controllers === 'number' &&
                    typeof firstRow.rooms === 'number' &&
                    typeof firstRow.status === 'string';
            case 'rooms':
                return typeof firstRow.name === 'string' &&
                    typeof firstRow.sensors === 'number' &&
                    typeof firstRow.controllers === 'number' &&
                    typeof firstRow.status === 'string' &&
                    !('rooms' in firstRow);
            default:
                return false;
        }
    };

    // Handle row navigation based on tab type
    const handleRowClick = (row: any, tabType?: string) => {
        // Guard against invalid id to avoid routes like /network-details/undefined
        if (!validateAndRedirect()) {
            return;
        }
        switch (tabType) {
            case 'zones':
                history.push(
                    `/network-details/${id}/zone/${encodeURIComponent(row.name)}${
                        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
                    }`
                );
                break;
            case 'rooms':
                history.push(
                    `/network-details/${id}/room-details/${encodeURIComponent(row.name)}${
                        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
                    }`
                );
                break;
            case 'sensors':
                // Pass sensor data through navigation state
                history.push({
                    pathname: `/network-details/${id}/sensor-details/${row.ldsu_uuid}-${row.SAID}`,
                    state: {
                        sensorDetails: row,
                        gatewayName: "Network Gateway",
                        portNumber: "1",
                        UUID: id,
                        sensorTimeRange: {
                            range: timeRange.range,
                            startDate: timeRange.startDate.valueOf(),
                            endDate: timeRange.endDate.valueOf(),
                        },
                    }
                });
                break;
            case 'controllers':
                // Navigate to controller details using a stable unique ID (UID-said) to avoid
                // collisions when multiple actuators share the same name (e.g. "Relay - Channel 1").
                // Fallback to name only if UID/said are not available.
                if (row.UID != null && row.said != null) {
                    history.push(
                        `/network-details/${id}/controller-details/${row.UID}-${row.said}`
                    );
                } else {
                    history.push(`/network-details/${id}/controller-details/${row.name}`);
                }
                break;
            case 'hubs':
                history.push(
                    `/network-details/${id}/hub-details/${encodeURIComponent(row.name)}${
                        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
                    }`
                );
                break;
            case 'auxiliaryHubs': {
                // Use a stable identifier (UUID) for navigation so that
                // renaming an auxiliary hub does not break deep links.
                const auxId = row.uuid ?? row.UUID ?? row.uid ?? row.UID ?? row.name;
                history.push(`/network-details/${id}/auxiliary-hub-details/${auxId}`);
                break;
            }
            case 'groups':
                history.push(
                    `/network-details/${id}/group-details/${encodeURIComponent(row.name)}${
                        row.uuid ? `?uuid=${encodeURIComponent(row.uuid)}` : ""
                    }`
                );
                break;
            default:
                break;
        }
    };

    const rowToContext: NetworkRowToContext = useMemo(
        () => ({ page: "network", networkId: id ?? "" }),
        [id]
    );
    const getRowTo = useCallback(
        (row: any, tabType?: string) => getRowToForContext(rowToContext, row, tabType),
        [rowToContext]
    );

    // Preprocess data for controllers tab - merge status and lastActive
    const processTabData = useCallback((key: string, data: any[]) => {
        if (getTabType('controllers', data)) {
            return data.map((row: any) => ({
                ...row,
                status: `${row.status} ${row.lastActive}`,
            }));
        }
        return data;
    }, []);

    const renderOnOffHighlighted = (text: string) => {
        if (!text) return '-';
        const parts = text.split(/\b(on|off)\b/i);
        let keyCounter = 0;
        return (
            <span>
                {parts.map((part) => {
                    const lower = part.toLowerCase();
                    const isTarget = lower === 'on' || lower === 'off';
                    const key = `onoff-${keyCounter++}`;
                    return isTarget ? (
                        <span key={key} style={{ color: '#00cbaf' }}>{part}</span>
                    ) : (
                        <span key={key}>{part}</span>
                    );
                })}
            </span>
        );
    };

    const renderOfflineHighlighted = (text: string) => {
        if (!text) return '';
        const parts = text.split(/(offline)/i);
        let keyCounter = 0;
        return (
            <span>
                {parts.map((part) => {
                    const key = `offline-${keyCounter++}`;
                    return part.toLowerCase() === 'offline' ? (
                        <span key={key} style={{ color: '#00cbaf' }}>{part}</span>
                    ) : (
                        <span key={key}>{part}</span>
                    );
                })}
            </span>
        );
    };

    // Helper function to calculate statusSortValue for controllers (handles object, numeric and string status)
    const calculateControllerStatusValue = useCallback((status: any, statusStr: string): number => {
        // Object form (e.g. { 0: 0, 1: 1 } indexed by SAID) – treat as "On" if any value is 1
        if (typeof status === "object" && status !== null && !Array.isArray(status)) {
            return Object.values(status).includes(1) ? 1 : 0;
        }

        // Explicit numeric / string 0/1 handling
        if (status === 1 || status === "1" || statusStr === "1") return 1;
        if (status === 0 || status === "0" || statusStr === "0") return 0;

        // Fallback for textual statuses: treat values starting with "on" as On
        return statusStr.startsWith("on") ? 1 : 0;
    }, []);

    // Helper function to calculate statusSortValue for different tab types
    const calculateStatusSortValue = useCallback((status: any, tabKey: string): string | number => {
        if (!status) return Number.POSITIVE_INFINITY;

        const statusStr = String(status).toLowerCase().trim();

        switch (tabKey) {
            case 'zones':
            case 'rooms':
            case 'groups':
                return statusStr || Number.POSITIVE_INFINITY;
            case 'controllers':
                return calculateControllerStatusValue(status, statusStr);
            case 'auxiliaryHubs':
                if (statusStr === 'unknown') return Number.POSITIVE_INFINITY;
                return statusStr === 'reachable' ? 1 : 0;
            case 'hubs':
                if (statusStr === 'unknown') return Number.POSITIVE_INFINITY;
                return statusStr === 'online' ? 1 : 0;
            default:
                return 0;
        }
    }, [calculateControllerStatusValue]);

    // Normalize auxiliary hubs to ensure 'uuid' key exists
    const normalizedAuxiliaryHubs = useMemo(() => {
        if (!network) return [];
        return (ldsuAuxiliaryHubs.length > 0 ? ldsuAuxiliaryHubs : network.auxiliaryHubs ?? []).map((hub: any) => ({
            ...hub,
            uuid: hub?.uuid ?? hub?.UUID ?? hub?.uid ?? hub?.UID ?? hub?.ldsu_uuid ?? '-',
            // Add statusSortValue for sorting
            statusSortValue: calculateStatusSortValue(hub.status, 'auxiliaryHubs')
        }));
    }, [network, ldsuAuxiliaryHubs, calculateStatusSortValue]);

    const getTabCountForHeader = (key: string): number => {
        if (key === 'sensors') {
            return rawSensorList.length;
        }
        if (key === 'controllers') {
            return rawActuatorList.length;
        }
        if (tabData[key]) {
            return tabData[key].length;
        }
        return 0;
    };

    const tabData: { [key: string]: any[] } = useMemo(() => {
        if (!network) {
            return {
                zones: [],
                rooms: [],
                groups: [],
                sensors: [],
                controllers: [],
                auxiliaryHubs: [],
                hubs: [],
            };
        }
        return {
            zones: (network.zones ?? []).map((zone: any) => ({
                ...zone,
                statusSortValue: calculateStatusSortValue(zone.status, 'zones')
            })),
            rooms: (network.rooms ?? []).map((room: any) => ({
                ...room,
                statusSortValue: calculateStatusSortValue(room.status, 'rooms')
            })),
            groups: (network.groups ?? []).map((group: any) => ({
                ...group,
                statusSortValue: calculateStatusSortValue(group.status, 'groups')
            })),
            sensors: filteredSensorList.length > 0 
                ? filteredSensorList.map((sensor: any) => ({
                    ...sensor,
                    // Add alertSortValue for sorting: 1 if alert is enabled, 0 if disabled
                    alertSortValue: returnCheckboxStatus(sensor.ldsu_uuid ?? sensor.UID ?? '', sensor.SAID ?? 0) ? 1 : 0
                }))
                : (network.sensors ?? []).map((sensor: any) => ({
                    ...sensor,
                    // Add alertSortValue for sorting: 1 if alert is enabled, 0 if disabled
                    alertSortValue: returnCheckboxStatus(sensor.ldsu_uuid ?? sensor.UID ?? '', sensor.SAID ?? 0) ? 1 : 0
                })),
            controllers: filteredActuatorList.length > 0 
                ? filteredActuatorList.map((actuator: any) => {
                    const { status, statusDisplayType } = getControllerStatusInfo(actuator);

                    const timestamp = actuator.readings?.timestamp ?? actuator.timestamp;
                    return {
                        name: actuator.name,
                        type: actuator.type,
                        status,
                        statusDisplayType,
                        info: actuator.info,
                        mode: actuator.info?.mode ?? "-",
                        UID: actuator.UID,
                        said: actuator.said,
                        timestamp,
                        statusSortValue: getControllerStatusSortValue(actuator),
                    };
                }) 
                : (network.controllers ?? []).map((controller: any) => {
                    const processed = processTabData("controllers", [controller])[0];
                    // processed.status may already be a combined string; keep it for display
                    const out = {
                        ...processed,
                        // Add statusSortValue for sorting based on original controller.status when available
                        statusSortValue: calculateStatusSortValue(controller.status ?? processed.status, "controllers"),
                    };

                    return out;
                }),
            auxiliaryHubs: normalizedAuxiliaryHubs,
            hubs: (network.hubs ?? []).map((hub: any) => ({
                ...hub,
                // Add statusSortValue for sorting: "Online" = 1, "Offline" = 0 (used in lastActive)
                statusSortValue: calculateStatusSortValue(hub.status, 'hubs')
            })),
        };
    }, [network, filteredSensorList, filteredActuatorList, normalizedAuxiliaryHubs, calculateStatusSortValue, processTabData, returnCheckboxStatus]);

    // Delete flow modals (shared by "not connected" and main view)
    const deleteModals = (
        <>
            <CommonModal
                show={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                title="Download Data"
                icon={null}
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setShowDownloadModal(false)}>
                            CANCEL
                        </Button>
                        <Button
                            style={{ background: "#00D1B2", borderColor: "#00D1B2" }}
                            onClick={() => {
                                setShowDownloadModal(false);
                                setModalShow(true);
                            }}
                        >
                            PROCEED
                        </Button>
                    </>
                }
            >
                <p>
                    Before deleting the network, ensure all network data (including sensors and actuators) has been downloaded.
                </p>
                <p>
                    By proceeding with delete network, you will permanently lose all associated data including sensors and actuators.
                </p>
            </CommonModal>
            <CommonModal
                show={modalShow}
                onClose={() => setModalShow(false)}
                title="Delete Network?"
                className="danger"
                icon={
                    <span className="material-icons danger">
                        delete
                    </span>
                }
                actions={
                    <>
                        <Button variant="secondary" onClick={() => setModalShow(false)}>
                            CANCEL
                        </Button>
                        <Button variant="danger" onClick={handleDeleteNetwork}>
                            DELETE
                        </Button>
                    </>
                }
            >
                <p>
                    Are you sure you want to delete <b>{network?.name ?? ""}</b>? This cannot be undone.
                </p>
            </CommonModal>
        </>
    );

    // Show "Network is not connected" state when no network data or no tab content
    if (!network ||
        Object.keys(network).length === 0 ||
        (!network.zones?.length && !network.sensors?.length && !network.controllers?.length &&
            !network.rooms?.length && !network.groups?.length && !network.auxiliaryHubs?.length && !network.hubs?.length)) {
        return (
            <>
            <ContentWrapper isLoading={isPageLoading}>
                <div className="page-content">
                    <Container fluid>
                        <Row>
                            <Col sm={6} className="NetworkDetails-header">
                                <Row>
                                    <Col>
                                        <h5 className="page-title">{network?.name ?? "-"}</h5>
                                    </Col>
                                    <Col sm="12">
                                        <Breadcrumb className="w-100">
                                            <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/networks" }}>
                                                All Networks
                                            </Breadcrumb.Item>
                                            <Breadcrumb.Item active>Network Details</Breadcrumb.Item>
                                        </Breadcrumb>
                                    </Col>
                                </Row>
                            </Col>
                            <Col sm={6}>
                                <Col className="networkdetails-buttons" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <HoverAuthorizeTooltip permission="gateway:delete">
                                        <Button
                                            variant="danger"
                                            style={{ marginRight: 8 }}
                                            onClick={() => setModalShow(true)}
                                        >
                                            <span>
                                                <img
                                                    className="mr-2 ml-2"
                                                    src={dltWhiteIcon}
                                                    alt="delete"
                                                />
                                                {" "}Delete
                                            </span>
                                        </Button>
                                    </HoverAuthorizeTooltip>
                                </Col>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={8}>
                                <div className="network-not-connected-panel" style={{
                                    backgroundColor: '#2f3a51',
                                    borderRadius: '8px',
                                    padding: '24px',
                                    marginBottom: '20px'
                                }}>
                                    <div style={{
                                        color: '#ff3860',
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        marginBottom: '16px'
                                    }}>
                                        Network is not connected.
                                    </div>
                                    <div style={{ color: '#a0a0a0', marginBottom: '20px' }}>
                                        There are two possible reasons why this network is not connected:
                                    </div>
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '8px' }}>
                                            Reason 1: The network is not yet set up by the system
                                        </div>
                                        <div style={{ color: '#a0a0a0', fontSize: '14px', lineHeight: '1.5' }}>
                                            The PSL Network you added may not have been linked by the system yet.Please check with your administrator to confirm that the PSL service is properly configured and connected to the IOTP system. Once set up, the network will be activated automatically.
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ color: '#ffffff', fontWeight: 'bold', marginBottom: '8px' }}>
                                            Reason 2: The Network ID entered may be incorrect
                                        </div>
                                        <div style={{ color: '#a0a0a0', fontSize: '14px', lineHeight: '1.5' }}>
                                            The ID you entered might be invalid or mistyped.
                                            To fix this, delete this gateway and try adding it again using the correct Network ID. You can find the Network ID in the PSL Mobile App (Settings &gt; Network Info).
                                        </div>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={4}>
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '1rem', paddingBottom: '1rem' }}>
                                    <div>
                                        <Form>
                                            <Form.Row>
                                                <Col md="12">
                                                    <Form.Label>
                                                        Network Name
                                                    </Form.Label>
                                                </Col>
                                                <Col
                                                    lg={8}
                                                    xs={12}
                                                    className="mb-2"
                                                >
                                                    <Form.Group className="mb-0">
                                                        <Form.Control
                                                            type="text"
                                                            required
                                                            aria-label="network-name"
                                                            value={networkName}
                                                            disabled={!isNetworkNameEditing}
                                                            onChange={(e) => {
                                                                setNetworkName(e.target.value);
                                                            }}
                                                            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleNetworkNameUpdate();
                                                                }
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={4} xs="12">
                                                    {isNetworkNameEditing ? (
                                                        <Button
                                                            type="button"
                                                            className="w-100"
                                                            onClick={handleNetworkNameUpdate}
                                                        >
                                                            UPDATE
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            className="w-100"
                                                            onClick={toggleNetworkNameEdit}
                                                        >
                                                            EDIT
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Form.Row>
                                        </Form>
                                    </div>
                                </div>
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1" style={{ color: '#ffffff' }}>Status</h5>
                                            <div style={{ textAlign: "right" }}>
                                                <span
                                                    style={{
                                                        color: "#ff3860",
                                                        marginRight: 8,
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    &#9679;
                                                </span>
                                                <span style={{ color: '#ffffff' }}>Not On-boarded</span>
                                                <IconButton
                                                    disableRipple
                                                    className="mr-2 ml-2 bigRefresh"
                                                    style={{ marginLeft: '8px', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                                                    aria-label="Refresh status"
                                                    onClick={() => {
                                                        // Try to refetch network data to see if it is connected now
                                                        fetchNetworkData();
                                                    }}
                                                >
                                                    <img src={refreshIcon} alt="refresh" />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-box network-info-group mb-2">
                                    <div>
                                        <div className="descriptor-item d-flex py-2" style={{ borderBottom: "1px solid #3e4b67" }}>
                                            <h6 className="flex-grow-1">Network ID</h6>
                                            <h6 className="ginfo float-right">{network?.id ?? '-'}</h6>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ContentWrapper>
            {deleteModals}
        </>
        );
    }


    // Helper function to handle alert toggle
    const handleAlertToggle = (ldsuUuid: string, said: number) => {
        const isChecked = returnCheckboxStatus(ldsuUuid, said);
        if (isChecked) {
            updateAlertStatus(ldsuUuid, said);
            return;
        }
        walletModalTrigger(() => updateAlertStatus(ldsuUuid, said));
    };

    // Sensor-specific custom renderers for TabTableWithSearch
    const renderSensorNameCell = (value: any, row: any) => {
        const to = getRowTo(row, "sensors");
        return (
            <span style={{ display: "flex", alignItems: "center" }}>
                <SensorIcon
                    sensorType={row.sensor_type ?? "Generic Analog Input"}
                    style={{ marginRight: 15 }}
                />
                <Link
                    to={to}
                    onClick={to === "#" ? (e) => {
                        e.preventDefault();
                        handleRowClick(row, "sensors");
                    } : undefined}
                >
                    {value}
                </Link>
            </span>
        );
    };

    const renderSensorReadingCell = (value: any, row: any) => {
        const formattedValue = formatSensorReadingCellValue(row, value);
        return (
            <span style={{ color: "#00d1b2" }}>
                {formattedValue}
            </span>
        );
    };

    const renderSensorMinMaxCell = (value: any, row: any) => {
        const formattedValue = formatSensorMinMaxCellValue(row);
        return <span>{formattedValue}</span>;
    };

    const renderSensorLastActiveCell = (value: any, row: any) => {
        const timestamp = row.readings?.timestamp
            ? moment(row.readings.timestamp, "X").calendar(getCalendarFormat())
            : value ?? "-";

        return (
            <span>
                {timestamp}
                <IconButton
                    disableRipple
                    className="mr-2 refresh-sensor"
                    style={{ cursor: "pointer", marginLeft: 8, background: "none", border: "none", padding: 0 }}
                    aria-label="Refresh sensor reading"
                    data-sensor-id={`${row.ldsu_uuid}-${row.SAID}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRefreshSensor(e, row);
                    }}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        handleRefreshSensor(e, row);
                    }}
                >
                    <img src={refreshIcon} alt="refresh" />
                </IconButton>
            </span>
        );
    };

    const renderSensorAlertCell = (value: any, row: any) => (
        <HoverAuthorizeTooltip permission="gateway:update">
            <div>
                <Form.Check
                    type="switch"
                    id={`switch-${row.ldsu_uuid}-${row.SAID}`}
                    label=""
                    checked={returnCheckboxStatus(
                        row.ldsu_uuid,
                        row.SAID
                    )}
                    disabled={returnDisabledStatus(
                        row.ldsu_uuid,
                        row.SAID
                    )}
                    onChange={() => handleAlertToggle(row.ldsu_uuid, row.SAID)}
                />
            </div>
        </HoverAuthorizeTooltip>
    );

    const getSensorCustomRenderers = () => ({
        sensor_name: renderSensorNameCell,
        reading: renderSensorReadingCell,
        minmax: renderSensorMinMaxCell,
        lastactive: renderSensorLastActiveCell,
        alert: renderSensorAlertCell,
    });

    const renderActuatorNameCell = (value: any, row: any) => {
        const to = getRowTo(row, "controllers");
        return (
            <span style={{ display: "flex", alignItems: "center" }}>
                <Link
                    to={to}
                    onClick={to === "#" ? (e) => {
                        e.preventDefault();
                        handleRowClick(row, "controllers");
                    } : undefined}
                >
                    {value}
                </Link>
            </span>
        );
    };

    const renderActuatorTypeCell = (value: any) => value ?? "-";

    const renderActuatorInfoCell = (value: any, row: any) => {
        if (row.info && typeof row.info === "object") {
            return row.info.auto ? "Auto" : "Manual";
        }
        return "-";
    };

    const renderActuatorModeCell = (value: any, row: any) => {
        if (typeof row.info === "object" && row.info?.mode) {
            return row.info.mode;
        }
        return "-";
    };

    const renderActuatorUIDCell = (value: any) => value ?? "-";

    const renderActuatorSaidCell = (value: any) => value ?? "-";

    const renderActuatorOBJCell = (value: any) => value ?? "-";

    const renderActuatorCLSCell = (value: any) => value ?? "-";

    const renderActuatorAppIdCell = (value: any) => value ?? "-";

    const renderActuatorCMDSCell = (value: any) => Array.isArray(value) ? value.length : "-";

    const renderActuatorTimestampCell = (value: any) => value ? moment(value, "X").calendar(getCalendarFormat()) : "-";

    // Actuator-specific custom renderers for TabTableWithSearch
    const getActuatorCustomRenderers = () => ({
        name: renderActuatorNameCell,
        type: renderActuatorTypeCell,
        info: renderActuatorInfoCell,
        mode: renderActuatorModeCell,
        UID: renderActuatorUIDCell,
        said: renderActuatorSaidCell,
        OBJ: renderActuatorOBJCell,
        CLS: renderActuatorCLSCell,
        appId: renderActuatorAppIdCell,
        CMDS: renderActuatorCMDSCell,
        timestamp: renderActuatorTimestampCell,
    });

    // Get hub column width configuration
    const getHubColumnWidth = (col: string) => {
        const colLower = col.toLowerCase();
        switch (colLower) {
            case 'name':
                return { sm: 2 }; // Hub name - largest column
            case 'uuid':
                return { sm: 3 }; // UUID - medium-large column
            case 'role':
                return { sm: 2 }; // Primary/Secondary - medium column
            case 'lastactive':
                return { sm: 3 }; // Last Active - smallest column
            default:
                return { sm: 2 }; // Default width for other columns
        }
    };

    // Get sensor columns configuration
    const getSensorColumns = () => [
        {
            label: 'Sensor Name',
            key: 'sensor_name',
            colProps: { sm: 2 }
        },
        {
            label: 'Type',
            key: 'sensor_type',
            colProps: { sm: 2 }
        },
        {
            label: 'Reading',
            key: 'reading',
            sortKey: 'readingSortValue',
            colProps: { sm: 1 }
        },
        {
            label: 'Agg Min / Max (Last 24 hrs)',
            key: 'minmax',
            sortKey: 'minMaxSortValue',
            colProps: { sm: 3 }
        },
        {
            label: (
                <span>
                    <span>Last Active</span>
                    <IconButton
                        disableRipple
                        className={
                            isRefreshing
                                ? "mr-2 bigRefresh refresh-animation"
                                : "mr-2 bigRefresh"
                        }
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginLeft: 8 }}
                        aria-label="Refresh all sensor readings"
                        onClick={(e) => {
                            e.stopPropagation();
                            refreshAllSensorReadings();
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            refreshAllSensorReadings();
                        }}
                    >
                        <img
                            src={refreshIcon}
                            alt="refresh"
                        />
                    </IconButton>
                </span>
            ),
            key: 'lastactive',
            sortKey: 'readings.timestamp',
            colProps: { sm: 2 }
        },
        {
            label: 'Alert',
            key: 'alert',
            sortKey: 'alertSortValue', // Use alertSortValue for proper sorting (1 = enabled, 0 = disabled)
            colProps: { sm: 1 }
        }
    ];

    // Helper to get default column keys for each tab type when no data is available
    const getDefaultColumnKeys = (tabKey: string): string[] => {
        switch (tabKey) {
            case 'zones':
                return ['name', 'sensors', 'controllers', 'rooms', 'status'];
            case 'rooms':
                return ['name', 'sensors', 'controllers', 'status'];
            case 'groups':
                return ['name', 'sensors', 'controllers', 'status'];
            case 'controllers':
                return ['name', 'type', 'status'];
            case 'auxiliaryHubs':
                return ['name', 'uuid', 'status'];
            case 'hubs':
                return ['name', 'uuid', 'role', 'lastActive'];
            default:
                return [];
        }
    };

    // Helper to get data source for column generation (prioritize tabData, fallback to network structure)
    const getDataForColumns = (tabKey: string) => {
        // First try tabData (filtered data)
        if (tabData[tabKey] && tabData[tabKey].length > 0) {
            return tabData[tabKey];
        }
        // Fallback to network structure (unfiltered data)
        const networkData = network?.[tabKey];
        if (Array.isArray(networkData) && networkData.length > 0) {
            return networkData;
        }
        // If no data, create a sample object with default keys for this tab type
        const defaultKeys = getDefaultColumnKeys(tabKey);
        if (defaultKeys.length > 0) {
            // Create a sample object with default keys so generateColumnsFromData can infer columns
            const sampleObject: any = {};
            for (const key of defaultKeys) {
                sampleObject[key] = null; // Placeholder value
            }
            return [sampleObject]; // Return array with one sample object
        }
        return null;
    };

    const formatStatusTimestamp = (row: any): string => {
        if (row.readings?.timestamp) {
            return moment(row.readings.timestamp, "X").calendar(getCalendarFormat());
        }
        if (row.timestamp) {
            return moment(row.timestamp, "X").calendar(getCalendarFormat());
        }
        return '01/01/1970 at 00:00:00';
    };

    const getStatusColor = (statusValue: string): string => {
        const statusLower = statusValue.toLowerCase();
        const isOnline = /(^|\s)on(line)?(\s|$)/i.test(statusLower);
        const isReachable = /^(reachable)$/i.test(statusLower.trim());
        const isNotReachable = /^(not\s+reachable)$/i.test(statusLower.trim());

        if (isOnline || (isReachable && !isNotReachable)) {
            return styledConst.Secondary_Green;
        }
        return styledConst.Secondary_Red;
    };

    const renderActuatorStatusCell = (
        statusValue: any,
        row: any,
        handleRefreshActuator: (
            e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
            row: any
        ) => void
    ): React.ReactNode => {
        const actuatorStatusValue = statusValue[row.said];

        if (actuatorStatusValue === undefined || actuatorStatusValue === null || actuatorStatusValue === "") {
            return (
                <span style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginLeft: 8 }}>-</span>
                    <IconButton
                        disableRipple
                        className="mr-2 bigRefresh"
                        style={{ cursor: "pointer", marginLeft: 8, background: "none", border: "none", padding: 0 }}
                        aria-label={`Refresh status for ${row.name ?? "this actuator"}`}
                        onClick={(e) =>
                            handleRefreshActuator(e as React.MouseEvent<HTMLElement>, row)
                        }
                        onKeyDown={(e) =>
                            handleRefreshActuator(e as React.KeyboardEvent<HTMLElement>, row)
                        }
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                </span>
            );
        }

        const isOnline = actuatorStatusValue === 1;
        const color = isOnline ? styledConst.Secondary_Green : styledConst.Secondary_Red;
        const statusText = isOnline ? 'On' : 'Off';
        const timestamp = formatStatusTimestamp(row);

        return (
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 8 }}>
                    <span style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: color,
                        marginRight: 6,
                        minWidth: 10,
                    }} />
                    <span style={{ color: '#00cbaf' }}>{statusText}</span> {timestamp}
                </span>
                <IconButton
                    disableRipple
                    className="mr-2 bigRefresh"
                    style={{ cursor: 'pointer', marginLeft: 8, background: 'none', border: 'none', padding: 0 }}
                    aria-label={`Refresh status for ${row.name ?? 'this actuator'}`}
                    onClick={(e) =>
                        handleRefreshActuator(e as React.MouseEvent<HTMLElement>, row)
                    }
                    onKeyDown={(e) =>
                        handleRefreshActuator(e as React.KeyboardEvent<HTMLElement>, row)
                    }
                >
                    <img src={refreshIcon} alt="refresh" />
                </IconButton>
            </span>
        );
    };

    const renderStringStatusCell = (
        statusValue: string,
        tabKey: string,
        handleRefreshActuator: (
            e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
            row: any
        ) => void,
        renderOnOffHighlighted: (text: string) => React.ReactNode,
        row: any
    ): React.ReactNode => {
        const trimmedStatus = (statusValue || "").trim();
        const isNoDataStatus = trimmedStatus === "-";

        if (isNoDataStatus) {
            return (
                <span style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginLeft: 8 }}>-</span>
                    {tabKey === "controllers" && (
                        <IconButton
                            disableRipple
                            className="mr-2 bigRefresh"
                            style={{
                                cursor: "pointer",
                                marginLeft: 8,
                                background: "none",
                                border: "none",
                                padding: 0,
                            }}
                            aria-label={`Refresh status for ${row.name ?? "this controller"}`}
                            onClick={(e) =>
                                handleRefreshActuator(e as React.MouseEvent<HTMLElement>, row)
                            }
                            onKeyDown={(e) =>
                                handleRefreshActuator(e as React.KeyboardEvent<HTMLElement>, row)
                            }
                        >
                            <img src={refreshIcon} alt="refresh" />
                        </IconButton>
                    )}
                </span>
            );
        }

        // IO Controller analog: show value only (no status icon, no on/off styling)
        if (tabKey === "controllers" && row.statusDisplayType === "value") {
            const timestamp = formatStatusTimestamp(row);
            return (
                <span style={{ display: "flex", alignItems: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 8 }}>
                        <span style={{ color: "#00cbaf" }}>{statusValue}</span>
                        {timestamp && <span style={{ marginLeft: 4 }}>{timestamp}</span>}
                    </span>
                    <IconButton
                        disableRipple
                        className="mr-2 bigRefresh"
                        style={{
                            cursor: "pointer",
                            marginLeft: 8,
                            background: "none",
                            border: "none",
                            padding: 0,
                        }}
                        aria-label={`Refresh status for ${row.name ?? "this controller"}`}
                        onClick={(e) =>
                            handleRefreshActuator(e as React.MouseEvent<HTMLElement>, row)
                        }
                        onKeyDown={(e) =>
                            handleRefreshActuator(e as React.KeyboardEvent<HTMLElement>, row)
                        }
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                </span>
            );
        }

        const color = getStatusColor(statusValue);
        const timestamp = tabKey === "controllers" ? formatStatusTimestamp(row) : "";

        // Only highlight "On"/"Off" text for tabs other than rooms/zones/groups
        const shouldHighlightOnOff =
            tabKey !== "rooms" && tabKey !== "zones" && tabKey !== "groups";

        return (
            <span style={{ display: "flex", alignItems: "center" }}>
                <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 8 }}>
                    <span
                        style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: color,
                            marginRight: 6,
                            minWidth: 10,
                        }}
                    />
                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                        {shouldHighlightOnOff
                            ? renderOnOffHighlighted(statusValue || "")
                            : statusValue}
                        {tabKey === "controllers" && timestamp && (
                            <span style={{ marginLeft: 4 }}>{timestamp}</span>
                        )}
                    </span>
                </span>
                {tabKey === "controllers" && (
                    <IconButton
                        disableRipple
                        className="mr-2 bigRefresh"
                        style={{
                            cursor: "pointer",
                            marginLeft: 8,
                            background: "none",
                            border: "none",
                            padding: 0,
                        }}
                        aria-label={`Refresh status for ${row.name ?? "this controller"}`}
                        onClick={(e) =>
                            handleRefreshActuator(e as React.MouseEvent<HTMLElement>, row)
                        }
                        onKeyDown={(e) =>
                            handleRefreshActuator(e as React.KeyboardEvent<HTMLElement>, row)
                        }
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                )}
            </span>
        );
    };

    const renderStatusCell = (
        row: any,
        col: string,
        tabKey: string,
        handleRefreshActuator: (
            e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
            row: any
        ) => void,
        renderOnOffHighlighted: (text: string) => React.ReactNode
    ): React.ReactNode => {
        const statusValue = row[col];

        // Handle actuator status (object with said property)
        if (statusValue && typeof statusValue === 'object' && row.said !== undefined) {
            return renderActuatorStatusCell(statusValue, row, handleRefreshActuator);
        }

        // Handle string status (traditional controllers)
        if (typeof statusValue === 'string') {
            return renderStringStatusCell(
                statusValue,
                tabKey,
                handleRefreshActuator,
                renderOnOffHighlighted,
                row
            );
        }

        // Fallback for other cases
        return statusValue ?? '-';
    };

    const createStatusRenderer = (
        col: string,
        tabKey: string,
        handleRefreshActuator: (
            e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
            row: any
        ) => void,
        renderOnOffHighlighted: (text: string) => React.ReactNode
    ): ColumnRenderer => {
        return (row: any) =>
            renderStatusCell(row, col, tabKey, handleRefreshActuator, renderOnOffHighlighted);
    };

    const renderLastActiveCell = (
        row: any,
        col: string,
        tabKey: string,
        handleRefreshSensor: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: any) => void,
        renderOfflineHighlighted: (text: string) => React.ReactNode
    ): React.ReactNode => {
        const value = String(row[col] ?? '');
        const isHubsTab = tabKey === 'hubs';

        // For hubs tab, merge status logic into lastActive
        if (isHubsTab) {
            const statusValue = row.status ?? '';
            const statusLower = String(statusValue).toLowerCase().trim();
            const isOnline = /online/i.test(statusLower);
            const circleClass = isOnline ? 'online' : 'offline';

            return (
                <span style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
                    <i className={`material-icons circle ${circleClass}`} style={{ marginRight: 6 }}>fiber_manual_record</i>
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {renderOfflineHighlighted(statusValue ?? value ?? 'Unknown')}
                    </span>
                </span>
            );
        }

        // For other tabs, use original logic
        return (
            <span style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 120 }}>
                    {value}
                </span>
                {(tabKey === 'sensors') && (
                    <IconButton
                        disableRipple
                        className="mr-2 refresh-sensor"
                        style={{ cursor: 'pointer', marginLeft: 8, background: 'none', border: 'none', padding: 0 }}
                        aria-label="Refresh sensor reading"
                        data-sensor-id={`${row.ldsu_uuid}-${row.SAID}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshSensor(e, row);
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            handleRefreshSensor(e, row);
                        }}
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                )}
            </span>
        );
    };

    const createLastActiveRenderer = (
        col: string,
        tabKey: string,
        handleRefreshSensor: (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: any) => void,
        renderOfflineHighlighted: (text: string) => React.ReactNode
    ): ColumnRenderer => {
        return (row: any) => renderLastActiveCell(row, col, tabKey, handleRefreshSensor, renderOfflineHighlighted);
    };

    const renderSensorNameCellForColumn = (row: any, col: string): React.ReactNode => {
        return (
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <SensorIcon
                    sensorType={row.type ?? row.sensorType ?? 'Generic Analog Input'}
                    style={{ marginRight: 15 }}
                />
                <span>{row[col]}</span>
            </span>
        );
    };

    const createSensorNameRenderer = (col: string): ColumnRenderer => {
        return (row: any) => renderSensorNameCellForColumn(row, col);
    };

    const renderHubNameCell = (
        row: any,
        col: string,
        handleRowClick: (row: any, tabType?: string) => void,
        getRowTo?: (row: any, tabType?: string) => string
    ): React.ReactNode => {
        const to = getRowTo?.(row, 'hubs') ?? "#";
        return (
            <span style={{ display: 'flex', alignItems: 'center' }}>
                <img
                    src={hubIcon}
                    alt="hub"
                    style={{ marginRight: 10 }}
                />
                <Link
                    to={to}
                    onClick={to === "#" ? (e) => {
                        e.preventDefault();
                        handleRowClick(row, 'hubs');
                    } : undefined}
                >
                    {row[col]}
                </Link>
            </span>
        );
    };

    const createHubNameRenderer = (
        col: string,
        handleRowClick: (row: any, tabType?: string) => void,
        getRowTo?: (row: any, tabType?: string) => string
    ): ColumnRenderer => {
        return (row: any) => renderHubNameCell(row, col, handleRowClick, getRowTo);
    };

    const renderUUIDCell = (row: any): React.ReactNode => {
        return (
            <span>{row.uuid ?? row.UUID ?? row.uid ?? row.UID ?? row.ldsu_uuid ?? '-'}</span>
        );
    };

    const createUUIDRenderer = (): ColumnRenderer => {
        return (row: any) => renderUUIDCell(row);
    };

    const formatColumnName = (col: string): string => {
        return col.replaceAll(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
    };

    const getStatusLabel = (col: string, tabKey: string): JSX.Element => {
        const showControllerRefresh = tabKey === 'controllers';
        const showEntityRefresh = tabKey === 'zones' || tabKey === 'rooms' || tabKey === 'groups';
        const isHeaderRefreshing = showEntityRefresh ? isEntityStatusRefreshing : isRefreshing;

        return (
            <span>
                <span>
                    {formatColumnName(col)}
                </span>
                {(showControllerRefresh || showEntityRefresh) && (
                    <IconButton
                        disableRipple
                        className={
                            isHeaderRefreshing
                                ? "mr-2 bigRefresh refresh-animation"
                                : "mr-2 bigRefresh"
                        }
                        style={{ cursor: 'pointer', marginLeft: 8, background: 'none', border: 'none', padding: 0 }}
                        aria-label={
                            showControllerRefresh
                                ? "Refresh all controller statuses"
                                : "Refresh all statuses"
                        }
                        onClick={() =>
                            showControllerRefresh
                                ? refreshAllActuatorReadings()
                                : refreshAllEntityStatuses(tabKey as EntityTabKey)
                        }
                        onKeyDown={() =>
                            showControllerRefresh
                                ? refreshAllActuatorReadings()
                                : refreshAllEntityStatuses(tabKey as EntityTabKey)
                        }
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                )}
            </span>
        );
    };

    const getLastActiveLabel = (col: string, tabKey: string): JSX.Element => {
        const showSensorRefresh = tabKey === 'sensors';
        const showHubRefresh = tabKey === 'hubs';
        const isLastActiveHeaderRefreshing = showHubRefresh ? isEntityStatusRefreshing : isRefreshing;
        const lastActiveRefreshClassName = isLastActiveHeaderRefreshing
            ? "mr-2 bigRefresh refresh-animation"
            : "mr-2 bigRefresh";
        return (
            <span style={{ paddingLeft: '10px' }}>
                <span>
                    {formatColumnName(col)}
                </span>
                {(showSensorRefresh || showHubRefresh) && (
                    <IconButton
                        disableRipple
                        className={lastActiveRefreshClassName}
                        style={{ cursor: 'pointer', marginLeft: 8, background: 'none', border: 'none', padding: 0 }}
                        aria-label={
                            showSensorRefresh
                                ? "Refresh all sensor readings"
                                : "Refresh all hub statuses"
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            showSensorRefresh
                                ? refreshAllSensorReadings()
                                : refreshAllEntityStatuses("hubs");
                        }}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            showSensorRefresh
                                ? refreshAllSensorReadings()
                                : refreshAllEntityStatuses("hubs");
                        }}
                    >
                        <img src={refreshIcon} alt="refresh" />
                    </IconButton>
                )}
            </span>
        );
    };

    const getMinMaxLabel = (): string => 'Agg Min / Max (Last 24 hrs)';

    const getNameLabel = (tabKey: string): string => {
        if (tabKey === 'controllers' || tabKey === 'auxiliaryHubs') {
            return 'Name';
        }
        return `${tabLabels[tabKey].slice(0, -1)} Name`;
    };

    const getTabSpecificLabel = (tabKey: string, colLower: string): string | undefined => {
        const labelMap: Record<string, Record<string, string>> = {
            hubs: {
                role: 'Primary/Secondary'
            },
            zones: {
                sensors: 'No. of Sensors',
                controllers: 'No. of Actuators',
                rooms: 'No. of Rooms'
            },
            rooms: {
                sensors: 'No. of Sensors',
                controllers: 'No. of Actuators'
            },
            groups: {
                sensors: 'No. of Sensors',
                controllers: 'No. of Actuators'
            }
        };

        return labelMap[tabKey]?.[colLower];
    };

    const getColumnLabel = (col: string, tabKey: string): string | JSX.Element => {
        const colLower = col.toLowerCase();
        
        // Status column with refresh icon for controllers
        if (colLower === 'status') {
            return getStatusLabel(col, tabKey);
        }
        
        // LastActive column with refresh icon for sensors
        if (colLower === 'lastactive' || colLower.includes('lastactive')) {
            return getLastActiveLabel(col, tabKey);
        }
        
        // MinMax column with time range dropdown for sensors
        if (colLower === 'minmax' || colLower.includes('minmax')) {
            return getMinMaxLabel();
        }
        
        // Name column
        if (col === "name") {
            return getNameLabel(tabKey);
        }
        
        // UUID column
        if (colLower === "uuid") {
            return "UUID";
        }
        
        // Tab-specific column labels
        const tabSpecificLabel = getTabSpecificLabel(tabKey, colLower);
        if (tabSpecificLabel) {
            return tabSpecificLabel;
        }
        
        // Default: format column name
        return formatColumnName(col);
    };

    const renderRoleCell = (row: any): React.ReactNode => {
        const role = row.role || '';
        // Add " Hub" to the role value (e.g., "Primary" -> "Primary Hub", "Secondary" -> "Secondary Hub")
        return role ? `${role} Hub` : '-';
    };

    const createRoleRenderer = (): ColumnRenderer => {
        return (row: any) => renderRoleCell(row);
    };

    const getColumnRender = (col: string, tabKey: string): ColumnRenderer | undefined => {
        const colLower = col.toLowerCase();

        if (colLower === 'status') {
            return createStatusRenderer(col, tabKey, handleRefreshActuator, renderOnOffHighlighted);
        }

        if (colLower === 'lastactive' || colLower.includes('lastactive')) {
            return createLastActiveRenderer(col, tabKey, handleRefreshSensor, renderOfflineHighlighted);
        }

        if (colLower === 'name' && tabKey === 'sensors') {
            return createSensorNameRenderer(col);
        }

        if (colLower === 'name' && tabKey === 'hubs') {
            return createHubNameRenderer(col, handleRowClick, getRowTo);
        }

        if (colLower === 'role' && tabKey === 'hubs') {
            return createRoleRenderer();
        }

        if (colLower === 'uuid' && tabKey === 'auxiliaryHubs') {
            return createUUIDRenderer();
        }

        return undefined;
    };

    // Helper function to get the correct column order for each tab
    const getColumnOrderForTab = (tabKey: string): string[] => {
        const orderMap: { [key: string]: string[] } = {
            zones: ['name', 'sensors', 'controllers', 'rooms', 'status'],
            rooms: ['name', 'sensors', 'controllers', 'status'],
            groups: ['name', 'sensors', 'controllers', 'status'],
            controllers: ['name', 'type', 'status'],
            auxiliaryHubs: ['name', 'uuid', 'status'],
            hubs: ['name', 'uuid', 'role', 'lastActive'],
        };
        return orderMap[tabKey] || [];
    };

    // Reusable function to generate columns from data
    const generateColumnsFromData = (data: any[], tabKey: string) => {
        if (!data || data.length === 0) return [];

        const allKeys = Object.keys(data[0]);
        const columnOrder = getColumnOrderForTab(tabKey);
        
        // Filter and get valid columns
        const validColumns = allKeys.filter((col: string) => {
            // Hide internal sorting fields (statusSortValue, alertSortValue)
            if (col.toLowerCase() === 'statussortvalue' || col.toLowerCase() === 'alertsortvalue') {
                return false;
            }
            // Hide lastActive column for controllers tab since it's merged into status
            if (tabKey === 'controllers' &&
                (col.toLowerCase() === 'lastactive' || col.toLowerCase().includes('lastactive'))) {
                return false;
            }
            // Hide uuid field from zone/room/group tables (kept only for navigation / auxiliary hubs)
            if ((tabKey === 'zones' || tabKey === 'rooms' || tabKey === 'groups') &&
                col.toLowerCase() === 'uuid') {
                return false;
            }
            // For controllers tab, only show Name, Type, and Status
            if (tabKey === 'controllers') {
                const allowedColumns = ['name', 'type', 'status'];
                return allowedColumns.includes(col.toLowerCase());
            }
            // For auxiliary hubs, only show Name, UUID, and Status
            if (tabKey === 'auxiliaryHubs') {
                const allowedColumns = ['name', 'uuid', 'status'];
                return allowedColumns.includes(col.toLowerCase());
            }
            // For hubs tab, hide status column (it will be merged into lastActive)
            if (tabKey === 'hubs' &&
                (col.toLowerCase() === 'status')) {
                return false;
            }
            return true;
        });

        // Sort columns according to the defined order, then append any remaining columns
        const sortedColumns = [...validColumns].sort((a, b) => {
            const aIndex = columnOrder.findIndex(order => order.toLowerCase() === a.toLowerCase());
            const bIndex = columnOrder.findIndex(order => order.toLowerCase() === b.toLowerCase());
            
            // If both are in the order array, sort by their position
            if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
            }
            // If only a is in the order array, it comes first
            if (aIndex !== -1) return -1;
            // If only b is in the order array, it comes first
            if (bIndex !== -1) return 1;
            // If neither is in the order array, maintain original order
            return 0;
        });

        return sortedColumns.map((col: string) => {
                let colProps;
                if (tabKey === 'auxiliaryHubs') {
                colProps = { sm: 3 };
                } else if (tabKey === 'hubs') {
                    colProps = getHubColumnWidth(col);
                } else {
                    colProps = undefined;
                }

                // Configure sortKey for status columns to use statusSortValue; for controllers tab match HubDetails behaviour
                const colLower = col.toLowerCase();
                const isStatusColumn = colLower === 'status';
                const isHubsLastActive = (colLower === 'lastactive' || colLower.includes('lastactive')) && tabKey === 'hubs';
                let sortKey: string | undefined;
                if (tabKey === 'controllers') {
                    // For controllers, sort directly on the display columns (name, type, status)
                    if (colLower === 'name' || colLower === 'type' || colLower === 'status') {
                        sortKey = col;
                    }
                } else {
                    // Other tabs continue to use statusSortValue / lastActive-based sorting
                    sortKey = (isStatusColumn || isHubsLastActive) ? 'statusSortValue' : undefined;
                }

                return {
                    label: getColumnLabel(col, tabKey),
                    key: col,
                    sortKey,
                    colProps,
                    render: getColumnRender(col, tabKey),
                };
            });
    };

    const getEmptyMessageForTab = (tabKey: string): string => {
        switch (tabKey) {
            case 'zones':
                return 'No Zones to display';
            case 'rooms':
                return 'No Rooms to display';
            case 'groups':
                return 'No Groups to display';
            case 'sensors':
                return 'No Sensors to display';
            case 'controllers':
                return 'No Actuators to display';
            case 'auxiliaryHubs':
                return 'No Auxiliary Hubs to display';
            case 'hubs':
                return 'No Hubs to display';
            default:
                return 'No data to display';
        }
    };

    const getColumnsForTab = (tabKey: string) => {
        // Always show the sensor table header (including the Min/Max time range header)
        // even when there is no sensor data, so users see the expected columns.
        if (tabKey === 'sensors') {
            return getSensorColumns();
        }
        const dataForColumns = getDataForColumns(tabKey);
        if (dataForColumns) {
            return generateColumnsFromData(dataForColumns, tabKey);
        }
        return [];
    };

    const getCustomRenderersForTab = (tabKey: string) => {
        if (tabKey === 'sensors' && filteredSensorList.length > 0) {
            return getSensorCustomRenderers();
        }
        if (tabKey === 'controllers' && filteredActuatorList.length > 0) {
            return getActuatorCustomRenderers();
        }
        return {};
    };

    const getCustomFiltersForTab = (tabKey: string) => {
        // Always show sensor filter on the Sensors tab, even when there is no data.
        if (tabKey === 'sensors') {
            return (
                <SensorTypeDropdown
                    sensorTypes={sensorTypes}
                    onChange={setSelectedSensorTypes}
                    selected={selectedSensorTypes}
                />
            );
        }
        // Always show actuator filter on the Controllers tab, even when there is no data.
        if (tabKey === 'controllers') {
            return (
                <ActuatorTypeDropdown
                    actuatorTypes={actuatorTypes}
                    onChange={setSelectedActuatorTypes}
                    selected={selectedActuatorTypes}
                />
            );
        }
        return undefined;
    };

    return (
        <>
        <ContentWrapper isLoading={isPageLoading}>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col sm={6} className="NetworkDetails-header">
                            <Row>
                                <Col>
                                    <h5 className="page-title">{network.name}</h5>
                                </Col>
                                <Col sm="12">
                                    <Breadcrumb className="w-100">
                                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/networks" }}>
                                            All Networks
                                        </Breadcrumb.Item>
                                        <Breadcrumb.Item active>Network Details</Breadcrumb.Item>
                                    </Breadcrumb>
                                </Col>
                            </Row>
                        </Col>
                        <Col sm={6}>
                            <Col className="networkdetails-buttons" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                <HoverAuthorizeTooltip permission="gateway:delete">
                                    <Button
                                        variant="danger"
                                        style={{ marginRight: 8 }}
                                        onClick={() => setShowDownloadModal(true)}
                                    >
                                        <span>
                                            <img
                                                className="mr-2 ml-2"
                                                src={dltWhiteIcon}
                                                alt="delete"
                                            />
                                            {" "}Delete
                                        </span>
                                    </Button>
                                </HoverAuthorizeTooltip>
                                <HoverAuthorizeTooltip permission="gateway:update">
                                    <Button
                                        variant="primary"
                                        onClick={handleScanNetwork}
                                        disabled={isScanning}
                                    >
                                        <span>
                                            <img
                                                className="mr-2 ml-1"
                                                src={searchIcon}
                                                alt="search"
                                            />
                                        </span>
                                        {isScanning ? "Scanning..." : "Scan"}
                                    </Button>
                                </HoverAuthorizeTooltip>
                            </Col>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={6}>
                            <div className="network-summary rounded shadow-sm">
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <Form>
                                            <Form.Row>
                                                <Col md="12">
                                                    <Form.Label>
                                                        Network Name
                                                    </Form.Label>
                                                </Col>
                                                <Col
                                                    lg={10}
                                                    xs={12}
                                                    className="mb-2"
                                                >
                                                    <Form.Group className="mb-0">
                                                        <Form.Control
                                                            type="text"
                                                            required
                                                            aria-label="network-name"
                                                            value={networkName}
                                                            disabled={!isNetworkNameEditing}
                                                            onChange={(e) => {
                                                                setNetworkName(e.target.value);
                                                            }}
                                                            onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    handleNetworkNameUpdate();
                                                                }
                                                            }}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col lg={2} xs="12">
                                                    {isNetworkNameEditing ? (
                                                        <Button
                                                            type="button"
                                                            className="w-100"
                                                            onClick={handleNetworkNameUpdate}
                                                        >
                                                            UPDATE
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type="button"
                                                            className="w-100"
                                                            onClick={toggleNetworkNameEdit}
                                                        >
                                                            EDIT
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Form.Row>
                                        </Form>
                                    </div>
                                </div>
                            </div>
                            <div className="network-summary rounded shadow-sm">
                                <div className="form-box mb-2" style={{ paddingLeft: '30px', paddingRight: '30px', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
                                    <div>
                                        <div className="descriptor-item d-flex gateway-info" style={{ borderBottom: "none" }}>
                                            <h5 className="flex-grow-1">Status</h5>
                                            <div style={{ textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                                <span style={{ display: "flex", alignItems: "center" }}>
                                                    {(() => {
                                                        if (!network.info) {
                                                            return (
                                                                <>
                                                                    <i className="material-icons circle offline">circle</i> Not On-boarded
                                                                </>
                                                            );
                                                        }
                                                        const isOnline = String(network.status).toLowerCase() === "online";
                                                        
                                                        return (
                                                            <>
                                                                <i
                                                                    className={`material-icons circle ${isOnline ? "online" : "offline"}`}
                                                                >
                                                                    circle
                                                                </i>{" "}
                                                                {isOnline ? (
                                                                    <>
                                                                        Online
                                                                    </>
                                                                ) : (
                                                                    "Offline"
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </span>
                                                <IconButton
                                                    disableRipple
                                                    className={
                                                        isRefreshing
                                                            ? "mr-2 bigRefresh refresh-animation"
                                                            : "mr-2 bigRefresh"
                                                    }
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                    aria-label="Refresh network status"
                                                    onClick={() => {
                                                        updateNetworkStatus();
                                                    }}
                                                    onKeyDown={() => {
                                                        updateNetworkStatus();
                                                    }}
                                                >
                                                    <img
                                                        src={refreshIcon}
                                                        alt="refresh"
                                                    />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="form-box network-info-group mb-2">
                                <div>
                                    <div className="descriptor-item d-flex py-2" style={{ borderBottom: "1px solid #3e4b67" }}>
                                        <h6 className="flex-grow-1">Network ID</h6>
                                        <h6 className="ginfo float-right">{network?.id || '-'}</h6>
                                    </div>
                                </div>
                            </div>
                        </Col>
                        <Col sm={6}>
                            <div>
                                {/* Download network data section replaced with DownloadDataUI */}
                                <div className="form-box download-network-data pb-3">
                                    <h5>Download network data</h5>
                                    <DownloadDataUI
                                        gatewayId={id}
                                        downloadType="downloadNetwork"
                                        emailLabel="Recieve Link"
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col sm={12}>
                            <div className="page-tabs networkdetails-tabs mt-3">
                                <Tab.Container
                                    id="network-details-tabs"
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(k ?? "zones")}
                                >
                                    <Row>
                                        <Col sm={12}>
                                            <Nav className="flex-nowrap" style={{ whiteSpace: 'nowrap' }}>
                                                {tabKeys.map((key) => (
                                                    <Nav.Item key={key}>
                                                        <Nav.Link eventKey={key}>
                                                            <h6>
                                                                {tabLabels[key]}{" "}
                                                                <span className="count">
                                                                    {getTabCountForHeader(key)}
                                                                </span>
                                                                {key === 'auxiliaryHubs' && ldsuAuxiliaryHubs.length > 0 && (
                                                                    <span style={{
                                                                        fontSize: '10px',
                                                                        color: '#00d1b2',
                                                                        marginLeft: '5px',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                    </span>
                                                                )}
                                                                {key === 'sensors' && rawSensorList.length > 0 && (
                                                                    <span style={{
                                                                        fontSize: '10px',
                                                                        color: '#00d1b2',
                                                                        marginLeft: '5px',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                    </span>
                                                                )}
                                                                {key === 'controllers' && rawActuatorList.length > 0 && (
                                                                    <span style={{
                                                                        fontSize: '10px',
                                                                        color: '#00d1b2',
                                                                        marginLeft: '5px',
                                                                        fontStyle: 'italic'
                                                                    }}>
                                                                    </span>
                                                                )}
                                                            </h6>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                ))}
                                            </Nav>
                                        </Col>
                                        <Col sm={12}>
                                            <Tab.Content>
                                                {tabKeys.map((key) => (
                                                    <Tab.Pane key={key} eventKey={key}>
                                                        <div style={{ borderRadius: 8, padding: 0 }}>
                                                            {key === 'sensors' && !IS_BASIC_PLAN && (
                                                                <WalletAlertComponent showWarningWhen="LowBalance" />
                                                            )}
                                                            <TabTableWithSearch
                                                                data={tabData[key] ?? []}
                                                                emptyMessage={getEmptyMessageForTab(key)}
                                                                columns={getColumnsForTab(key)}
                                                                placeholder={`Search...`}
                                                                onRowClick={handleRowClick}
                                                                getRowTo={getRowTo}
                                                                tabType={key}
                                                                showArrowColumn={true}
                                                                customRenderers={getCustomRenderersForTab(key)}
                                                                customFilters={getCustomFiltersForTab(key)}
                                                            />
                                                        </div>
                                                    </Tab.Pane>
                                                ))}
                                            </Tab.Content>
                                        </Col>
                                    </Row>
                                </Tab.Container>
                            </div>
                        </Col>

                    </Row>
                </Container>
            </div>
            <LoadingModal showModal={isAlertLoading} />
        </ContentWrapper>
        {deleteModals}
        </>
    );
};

export default NetworkDetails; 